// src/components/clients/NewClientModal.jsx
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  createClientNote,
  getClientById,
  getClientServiceLog,
  getClientTickets,
  listClientEmailPreferences,
  listClientEmailMessages,
  listClientNotes,
  updateClientEmailPreference,
} from "../../api/clients";
import { getClientEmailPreferenceLink } from "../../api/emailPreferences";
import { listStaff } from "../../api/staffs";
import { getClientServiceLogReviews } from "../../api/clientReviews";
import {
  getClientRewardTransactions,
  getClientRewards,
  getRewardsSettings,
  linkClientReferral,
  lookupReferralCode,
} from "../../api/rewards";
import { listClientGiftCards } from "../../api/giftCards";
import ClientNotesPanel from "./ClientNotesPanel";
import GiftCardDetailsModal from "../modals/GiftCardDetailsModal";
import SBINModal from "../modals/SBINModal";
import ModalCloseX from "../common/ModalCloseX";
import {
  cancelAppointment,
  listAppointments,
  sendFutureAppointmentsEmail,
} from "../../api/appointments";
import { formatShortDate } from "../../utils/formatShortDate";
import { formatInTimeZone } from "../../utils/datetime";
import { getSalonTimezone } from "../../utils/salonTimezone";
import {
  formatClientSpent,
  formatLastVisitAge,
  getClientMetrics,
} from "../../utils/clientMetrics";
import InfoTooltip from "../common/InfoTooltip";
import {
  APPOINTMENT_TYPE_OPTIONS,
  appointmentTypeDescription,
  appointmentTypeLabel,
  normalizeAppointmentType,
} from "../../constants/appointmentTypes";
import {
  formatNANP,
  isValidNANP,
  toNANPDigits10,
} from "../../utils/phone";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_OUTLINE,
  BTN_PRIMARY,
  TAB_BASE,
  TAB_ACTIVE,
  TAB_INACTIVE,
} from "../../ui/modalStyles";
import useMyPermissions from "../../hooks/useMyPermissions";
import { useAuth } from "../../context/AuthContext";
import { formatPermissionError } from "../../utils/permissionErrorMessages";

const TAB_PROFILE = "profile";
const TAB_FUTURE_APPTS = "future_appts";
const TAB_SERVICES = "services";
const TAB_TICKETS = "tickets";
const TAB_GIFT_CARDS = "gift_cards";
const TAB_NOTES = "notes";
const TAB_REWARDS = "rewards";
const TAB_EMAILS = "emails";
const APPOINTMENTS_CHANGED_EVENT = "sbin:appointmentsChanged";
const CLIENT_SERVICE_LOG_CHANGED_EVENT = "sbin:clientServiceLogChanged";
const STATE_PROVINCE_OPTIONS = [
  "",
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
];
const GENDER_PRONOUN_OPTIONS = [
  "",
  "Male",
  "Female",
  "She/Her/Hers",
  "He/Him/His",
  "They/Them/Theirs",
  "She and/or He",
  "Non-Binary",
];
const CLIENT_LABEL_OPTIONS = ["VIP", "TOP CLIENT", "FRIEND", "FOLLOW-UP"];
const HEALTH_CONCERN_NOTE_TYPE = "HEALTH_CONCERN";
const PORTAL_HEALTH_CONCERN_NOTE_TYPE = "HEALTH_CONCERN_PORTAL";

function splitName(fullName) {
  if (!fullName) return { firstName: "", lastName: "" };
  const parts = fullName.trim().split(" ");
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }
  const [first, ...rest] = parts;
  return { firstName: first, lastName: rest.join(" ").trim() };
}

const StarIcon = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={`h-4 w-4 ${filled ? "text-amber-400" : "text-slate-300"}`}
  >
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 0 0 .95.69h4.158c.969 0 1.371 1.24.588 1.81l-3.363 2.444a1 1 0 0 0-.364 1.118l1.287 3.955c.3.921-.755 1.688-1.54 1.118l-3.364-2.444a1 1 0 0 0-1.175 0l-3.364 2.444c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 0 0-.364-1.118L2.05 9.382c-.783-.57-.38-1.81.588-1.81h4.159a1 1 0 0 0 .95-.69l1.302-3.955Z" />
  </svg>
);

const renderStars = (value) => {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= 0) {
    return <span className="text-xs text-slate-400">--</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, idx) => (
        <StarIcon key={idx} filled={idx < rating} />
      ))}
      <span className="text-xs text-slate-500">{rating}/5</span>
    </div>
  );
};

function parseNotes(rawNotes) {
  // Best-effort parse of previous preference block so we can prefill form.
  if (!rawNotes) {
    return {
      preferredContact: "",
      receiveReminders: false,
      receiveMarketing: false,
      notesText: "",
    };
  }

  const lines = rawNotes.split("\n").map((l) => l.trim());
  let preferredContact = "";
  let receiveReminders = false;
  let receiveMarketing = false;

  lines.forEach((line) => {
    const lowerLine = line.toLowerCase();
    if (lowerLine.startsWith("preferred contact")) {
      preferredContact = line.replace(/preferred contact:\s*/i, "").trim();
    }
    if (lowerLine.startsWith("appointment reminders")) {
      receiveReminders = lowerLine.includes("yes");
    }
    if (lowerLine.startsWith("marketing")) {
      receiveMarketing = lowerLine.includes("yes");
    }
  });

  // Pull out text after "[Notes]" if present, otherwise leave the original notes.
  const notesSectionIndex = lines
    .map((l) => l.toLowerCase())
    .findIndex((l) => l.startsWith("[notes]"));
  let notesText = rawNotes;
  if (notesSectionIndex >= 0) {
    notesText = lines.slice(notesSectionIndex + 1).join("\n").trim();
  }

  return {
    preferredContact,
    receiveReminders,
    receiveMarketing,
    notesText,
  };
}

function normalizeDateInput(value) {
  if (!value) return "";
  if (typeof value !== "string") return "";
  if (value.includes("T")) {
    return value.split("T")[0];
  }
  if (value.includes(" ")) {
    return value.split(" ")[0];
  }
  return value;
}

function formatDateInput(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDateShort(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function emailMessageTypeLabel(value) {
  const labels = {
    appointment_confirmation: "Appointment confirmation",
    appointment_reminder: "Appointment reminder",
    future_appointments: "Future appointments",
    ticket_receipt: "Ticket receipt",
    review_request: "Review request",
    review_reminder: "Review reminder",
    client_survey: "Client survey",
    feedback_followup: "Feedback follow-up",
    feedback_reply: "Client reply",
    public_review_ask: "Public review ask",
    refill_reminder: "Refill reminder",
    marketing_campaign: "Marketing campaign",
    marketing_opt_in_invite: "Email opt-in invite",
    marketing_opt_in_feedback: "Email preference feedback",
  };
  const key = String(value || "").trim();
  if (labels[key]) return labels[key];
  return key ? key.replaceAll("_", " ") : "Email";
}

function emailStatusClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (["sent", "received"].includes(normalized)) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (["skipped", "skipped_unsubscribed"].includes(normalized)) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (["failed", "bounced"].includes(normalized)) {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }
  return "bg-slate-50 text-slate-600 border-slate-200";
}

const EMAIL_PREFERENCE_COPY = {
  all_optional: {
    title: "All optional emails",
    description: "Stops all feedback, reminder, refill, and promotion emails.",
  },
  feedback: {
    title: "Feedback requests",
    description: "Review requests, surveys, and feedback follow-ups.",
  },
  appointment_reminders: {
    title: "Appointment reminders",
    description: "Upcoming appointment reminders and future appointment summaries.",
  },
  service_reminders: {
    title: "Refill and service reminders",
    description: "Service refill and maintenance reminders.",
  },
  promotions: {
    title: "Promotions and salon updates",
    description: "Campaigns, offers, announcements, and email opt-in messages.",
  },
};

const EMAIL_PREFERENCE_ORDER = [
  "all_optional",
  "feedback",
  "appointment_reminders",
  "service_reminders",
  "promotions",
];

function preferenceSourceLabel(source) {
  const normalized = String(source || "").toLowerCase();
  if (normalized === "client") return "Updated by client";
  if (normalized === "staff") return "Updated by salon";
  if (normalized === "system") return "System updated";
  return "Default setting";
}

function formatServiceStatusLabel(rawStatus) {
  const s = String(rawStatus || "scheduled").toLowerCase().trim();
  if (s === "completed" || s === "closed" || s === "complete") return "completed";
  if (s === "checked_in" || s === "checked-in" || s === "check_in") return "check in";
  if (s === "confirmed") return "confirmed";
  if (s === "scheduled" || s === "" || s === "none") return "scheduled";
  return s.replaceAll("_", " ");
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

function formatSignedMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  const absValue = Math.abs(num).toFixed(2);
  return `${num < 0 ? "-" : ""}$${absValue}`;
}

export default function NewClientModal({
  isOpen,
  mode = "create",
  initialClient = null,
  salons = [],
  canEdit = false,
  restrictedView = null,
  preferredSalonId = null,
  requireContactInfo = false,
  initialTab = null,
  initialSection = null,
  prefill = null,
  canViewClientMetrics: canViewClientMetricsProp = null,
  onClose,
  onSave,
  onCreated,
  onOpenNewAppointment,
}) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const currentRole = (localStorage.getItem("role") || "").toLowerCase();
  const isTechnicianRole = currentRole === "technician";
  const { permissions: modalPermissions } = useMyPermissions({
    enabled: true,
  });
  const canViewClientContact =
    modalPermissions === null
      ? !isTechnicianRole
      : Boolean(
          modalPermissions?.["clients.view_contact"] ||
            modalPermissions?.["clients.manage"]
        );
  const canViewClientMetrics =
    canViewClientMetricsProp == null
      ? modalPermissions === null
        ? [
            "admin",
            "host",
            "owner",
            "manager",
            "reception",
            "receptionist",
            "independent_professional",
            "independent professional",
            "independent-professional",
            "independent_operator",
            "solo_technician",
          ].includes(currentRole)
        : Boolean(modalPermissions?.["clients.view_metrics"])
      : Boolean(canViewClientMetricsProp);
  const isHealthSafetyOnlyView =
    String(restrictedView || "").trim().toLowerCase() === "health_safety";
  const showPhoneField = canViewClientContact;
  const houseAccountMessage =
    "House Account is a system client. No modifications are allowed.";

  const [activeTab, setActiveTab] = useState(TAB_PROFILE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [houseToast, setHouseToast] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [requiredSubmitNotice, setRequiredSubmitNotice] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredStaffId, setPreferredStaffId] = useState("");
  const [clientLabel, setClientLabel] = useState("");
  const [preferredStaffOptions, setPreferredStaffOptions] = useState([]);
  const [preferredStaffLoading, setPreferredStaffLoading] = useState(false);
  const [preferredContact, setPreferredContact] = useState("");
  const [receiveReminders, setReceiveReminders] = useState(false);
  const [receiveMarketing, setReceiveMarketing] = useState(false);
  const [notes, setNotes] = useState("");
  const [isDiabetic, setIsDiabetic] = useState(false);
  const [diabeticFlaggedAt, setDiabeticFlaggedAt] = useState(null);
  const [hasHealthConcerns, setHasHealthConcerns] = useState(false);
  const [healthConcernsFlaggedAt, setHealthConcernsFlaggedAt] = useState(null);
  const [healthConcernNoteText, setHealthConcernNoteText] = useState("");
  const [healthConcernNotes, setHealthConcernNotes] = useState([]);
  const [healthConcernNotesLoading, setHealthConcernNotesLoading] = useState(false);
  const [healthConcernNotesError, setHealthConcernNotesError] = useState("");
  const [healthConcernNotesSaving, setHealthConcernNotesSaving] = useState(false);
  const [salonId, setSalonId] = useState(null);
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [married, setMarried] = useState("");
  const [genderPronouns, setGenderPronouns] = useState("");
  const [birthday, setBirthday] = useState("");
  const [ocsAccountLocked, setOcsAccountLocked] = useState(false);
  const [ocsUsername, setOcsUsername] = useState("");
  const [ocsPassword, setOcsPassword] = useState("");
  const [serviceLog, setServiceLog] = useState([]);
  const [serviceLogLoading, setServiceLogLoading] = useState(false);
  const [serviceLogError, setServiceLogError] = useState("");
  const [clientGiftCards, setClientGiftCards] = useState([]);
  const [clientGiftCardsLoading, setClientGiftCardsLoading] = useState(false);
  const [clientGiftCardsError, setClientGiftCardsError] = useState("");
  const [giftCardModalCode, setGiftCardModalCode] = useState(null);
  const [filterBy, setFilterBy] = useState("appointment_type");
  const [filterText, setFilterText] = useState("");
  const [filterApptType, setFilterApptType] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [futureAppts, setFutureAppts] = useState([]);
  const [futureApptsLoading, setFutureApptsLoading] = useState(false);
  const [futureApptsError, setFutureApptsError] = useState("");
  const [futureApptsNotice, setFutureApptsNotice] = useState("");
  const [cancelingAppointmentId, setCancelingAppointmentId] = useState(null);
  const [emailAppointmentOpen, setEmailAppointmentOpen] = useState(false);
  const [emailAppointmentValue, setEmailAppointmentValue] = useState("");
  const [emailAppointmentError, setEmailAppointmentError] = useState("");
  const [emailAppointmentSending, setEmailAppointmentSending] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const notesPanelRef = useRef(null);
  const healthSafetySectionRef = useRef(null);
  const today = useMemo(() => formatDateInput(new Date()), []);
  const [ticketRows, setTicketRows] = useState([]);
  const [ticketRowsLoading, setTicketRowsLoading] = useState(false);
  const [ticketRowsError, setTicketRowsError] = useState("");
  const [reviewRows, setReviewRows] = useState([]);
  const [reviewRowsLoading, setReviewRowsLoading] = useState(false);
  const [reviewRowsError, setReviewRowsError] = useState("");
  const [reviewExpanded, setReviewExpanded] = useState({});
  const [reviewDetailOpen, setReviewDetailOpen] = useState(false);
  const [reviewDetail, setReviewDetail] = useState(null);
  const [emailMessages, setEmailMessages] = useState([]);
  const [emailMessagesLoading, setEmailMessagesLoading] = useState(false);
  const [emailMessagesError, setEmailMessagesError] = useState("");
  const [expandedEmailMessages, setExpandedEmailMessages] = useState({});
  const [emailPreferences, setEmailPreferences] = useState([]);
  const [emailPreferencesLoading, setEmailPreferencesLoading] = useState(false);
  const [emailPreferencesError, setEmailPreferencesError] = useState("");
  const [emailPreferencesSaving, setEmailPreferencesSaving] = useState("");
  const [emailPreferenceLinkLoading, setEmailPreferenceLinkLoading] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketStartDate, setTicketStartDate] = useState(today);
  const [ticketEndDate, setTicketEndDate] = useState(today);
  const [rewardsData, setRewardsData] = useState(null);
  const [rewardsTransactions, setRewardsTransactions] = useState([]);
  const [rewardsSettings, setRewardsSettings] = useState(null);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsError, setRewardsError] = useState("");
  const [referralInput, setReferralInput] = useState("");
  const [referralLookup, setReferralLookup] = useState(null);
  const [referralLookupError, setReferralLookupError] = useState("");
  const [referralBlockInfo, setReferralBlockInfo] = useState(null);
  const [referralNotice, setReferralNotice] = useState("");
  const [referralLinking, setReferralLinking] = useState(false);
  const [copyNotice, setCopyNotice] = useState("");
  const [resolvedClient, setResolvedClient] = useState(initialClient);
  const [loadingClientRecord, setLoadingClientRecord] = useState(false);
  const formSeededForOpenRef = useRef(false);
  const [refillReminderEnabled, setRefillReminderEnabled] = useState(false);
  const [refillIntervalWeeks, setRefillIntervalWeeks] = useState(3);
  const [refillLastServiceDate, setRefillLastServiceDate] = useState(null);
  const [refillNextSendAtUtc, setRefillNextSendAtUtc] = useState(null);

  const isEdit = mode === "edit";
  const hydratedClient = resolvedClient || initialClient;
  const clientMetrics = useMemo(() => getClientMetrics(hydratedClient), [hydratedClient]);
  const isHouseAccount =
    isEdit &&
    (initialClient?.full_name || "").trim().toLowerCase() === "house account";
  const canEditClient = canEdit && !isHouseAccount;
  const emailPreferenceRows = useMemo(() => {
    const byCategory = new Map(
      emailPreferences.map((pref) => [pref.category, pref])
    );
    return EMAIL_PREFERENCE_ORDER.map((category) => {
      const copy = EMAIL_PREFERENCE_COPY[category] || {};
      const pref = byCategory.get(category);
      return {
        category,
        label: copy.title || pref?.label || category.replaceAll("_", " "),
        description: copy.description || "",
        is_subscribed: pref ? Boolean(pref.is_subscribed) : true,
        source: pref?.source || "default",
        reason: pref?.reason || "",
        updated_at_utc: pref?.updated_at_utc || "",
      };
    });
  }, [emailPreferences]);
  const allOptionalSubscribed = emailPreferenceRows.find(
    (pref) => pref.category === "all_optional"
  )?.is_subscribed !== false;
  const houseToastTimer = useRef(null);
  const lastHouseToastAt = useRef(0);
  const resolveInitialTab = (tabValue) => {
    if (isHealthSafetyOnlyView) {
      return TAB_PROFILE;
    }
    switch (tabValue) {
      case "notes":
        return TAB_NOTES;
      case "future":
        return TAB_FUTURE_APPTS;
      case "services":
        return TAB_SERVICES;
      case "tickets":
        return TAB_TICKETS;
      case "giftcards":
        return TAB_GIFT_CARDS;
      case "rewards":
        return TAB_REWARDS;
      case "emails":
      case "messages":
        return TAB_EMAILS;
      case "profile":
      default:
        return TAB_PROFILE;
    }
  };

  const effectiveSalonId = useMemo(() => {
    if (salonId) return salonId;
    if (!salons || salons.length === 0) return null;
    return salons[0].id;
  }, [salonId, salons]);
  const hasClientId = Boolean(initialClient?.id);
  const appointmentClient = useMemo(() => {
    if (!hydratedClient?.id) return null;

    const nextFirstName = (firstName || hydratedClient.first_name || "").trim();
    const nextLastName = (lastName || hydratedClient.last_name || "").trim();
    const nextFullName =
      `${nextFirstName} ${nextLastName}`.trim() ||
      hydratedClient.full_name ||
      `Client #${hydratedClient.id}`;

    return {
      ...hydratedClient,
      first_name: nextFirstName,
      last_name: nextLastName,
      full_name: nextFullName,
      phone: phone || hydratedClient.phone || "",
    };
  }, [firstName, hydratedClient, lastName, phone]);
  const canOpenNewAppointment =
    hasClientId &&
    !isHouseAccount &&
    typeof onOpenNewAppointment === "function" &&
    Boolean(appointmentClient?.id);
  const currentSalon = useMemo(() => {
    if (!salons || salons.length === 0) return null;
    const targetId =
      hydratedClient?.salon_id || preferredSalonId || effectiveSalonId;
    if (!targetId) return salons[0] || null;
    return salons.find((salon) => salon.id === targetId) || salons[0] || null;
  }, [effectiveSalonId, hydratedClient?.salon_id, preferredSalonId, salons]);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const healthConcernClientId = hydratedClient?.id || initialClient?.id || null;

  const diabeticFlaggedDateLabel = useMemo(() => {
    if (!diabeticFlaggedAt) return "";
    const label = formatInTimeZone(
      diabeticFlaggedAt,
      salonTimezone,
      { month: "short", day: "numeric", year: "numeric" },
      "en-US"
    );
    return label && label !== "-" ? label : "";
  }, [diabeticFlaggedAt, salonTimezone]);

  const healthConcernsFlaggedDateLabel = useMemo(() => {
    if (!healthConcernsFlaggedAt) return "";
    const label = formatInTimeZone(
      healthConcernsFlaggedAt,
      salonTimezone,
      { month: "short", day: "numeric", year: "numeric" },
      "en-US"
    );
    return label && label !== "-" ? label : "";
  }, [healthConcernsFlaggedAt, salonTimezone]);

  const serviceWaiverAcceptedDateLabel = useMemo(() => {
    const acceptedAt = hydratedClient?.service_waiver_accepted_at;
    if (!acceptedAt) return "";
    const label = formatInTimeZone(
      acceptedAt,
      salonTimezone,
      {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      },
      "en-US"
    );
    return label && label !== "-" ? label : "";
  }, [hydratedClient?.service_waiver_accepted_at, salonTimezone]);

  const fetchFutureAppointments = useCallback(
    async (clientId) => {
      if (!clientId) return;
      const resolvedSalonId =
        initialClient?.salon_id || preferredSalonId || effectiveSalonId;
      setFutureApptsLoading(true);
      setFutureApptsError("");
      try {
        const params = {
          client_id: clientId,
          only_future: true,
          exclude_cancelled: true,
        };
        if (resolvedSalonId) {
          params.salon_id = resolvedSalonId;
        }
        const appts = await listAppointments(params);
        const normalized = Array.isArray(appts) ? appts : [];
        const mapped = normalized.map((appt) => ({
          id: appt.id,
          appointment_id: appt.id,
          start_time: appt.start_time,
          end_time: appt.end_time,
          status: appt.status,
          service_name:
            appt.service?.name ||
            appt.service_name ||
            null,
          with_name:
            appt.staff_member?.display_name ||
            appt.staff_member?.name ||
            appt.with_name ||
            null,
          scheduled_by_name: appt.scheduled_by_name || null,
        }));
        setFutureAppts(mapped);
      } catch (err) {
        console.error(err);
        setFutureApptsError("Unable to load future appointments.");
      } finally {
        setFutureApptsLoading(false);
      }
    },
    [effectiveSalonId, initialClient?.salon_id, preferredSalonId]
  );

  const futureAppointments = useMemo(() => {
    const now = new Date();
    return (futureAppts || [])
      .map((entry) => ({
        ...entry,
        _date: entry?.start_time ? new Date(entry.start_time) : null,
        _status: (entry?.status || "").toLowerCase(),
      }))
      .filter(
        (entry) =>
          entry._date &&
          entry._date >= now &&
          entry._status !== "cancelled"
      )
      .sort((a, b) => a._date - b._date);
  }, [futureAppts]);

  const currentClientEmail = useMemo(
    () =>
      String(
        email ||
          hydratedClient?.email ||
          initialClient?.email ||
          resolvedClient?.email ||
          ""
      ).trim(),
    [email, hydratedClient?.email, initialClient?.email, resolvedClient?.email]
  );

  const validateEmailAddress = useCallback((value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "Email is required.";
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    return isValid ? "" : "Enter a valid email address.";
  }, []);

  const handleCancelFutureAppointment = useCallback(
    async (appointmentId) => {
      if (!appointmentId || !initialClient?.id) return;
      setCancelingAppointmentId(appointmentId);
      setFutureApptsError("");
      setFutureApptsNotice("");
      try {
        await cancelAppointment(appointmentId);
        await fetchFutureAppointments(initialClient.id);
        setFutureApptsNotice("Appointment cancelled.");
        window.dispatchEvent(new CustomEvent(APPOINTMENTS_CHANGED_EVENT));
      } catch (err) {
        console.error(err);
        setFutureApptsError(err?.message || "Unable to cancel appointment.");
      } finally {
        setCancelingAppointmentId(null);
      }
    },
    [fetchFutureAppointments, initialClient?.id]
  );

  const closeEmailAppointmentModal = useCallback((force = false) => {
    if (emailAppointmentSending && !force) return;
    setEmailAppointmentOpen(false);
    setEmailAppointmentValue("");
    setEmailAppointmentError("");
  }, [emailAppointmentSending]);

  const openEmailAppointmentModal = useCallback(
    () => {
      setEmailAppointmentValue(currentClientEmail);
      setEmailAppointmentError("");
      setEmailAppointmentOpen(true);
    },
    [currentClientEmail]
  );

  const handleSendAppointmentEmail = useCallback(async () => {
    if (!initialClient?.id) return;
    const appointmentIds = futureAppointments
      .map((entry) => entry?.appointment_id || entry?.id)
      .filter(Boolean);
    if (!appointmentIds.length) {
      setEmailAppointmentError("There are no future appointments to email.");
      return;
    }

    const trimmedEmail = String(emailAppointmentValue || "").trim();
    const validationMessage = validateEmailAddress(trimmedEmail);
    if (validationMessage) {
      setEmailAppointmentError(validationMessage);
      return;
    }

    const shouldSaveToClient = !currentClientEmail;
    setEmailAppointmentSending(true);
    setEmailAppointmentError("");
    setFutureApptsError("");
    setFutureApptsNotice("");
    try {
      const result = await sendFutureAppointmentsEmail({
        appointment_ids: appointmentIds,
        email: trimmedEmail,
        save_to_client: shouldSaveToClient,
      });

      if (shouldSaveToClient) {
        setEmail(trimmedEmail);
        setResolvedClient((prev) =>
          prev ? { ...prev, email: trimmedEmail } : { ...(initialClient || {}), email: trimmedEmail }
        );
      }

      setFutureApptsNotice(
        `Future appointments email sent${result?.email ? ` to ${result.email}` : ""}${
          result?.appointment_count ? ` for ${result.appointment_count} appointment${result.appointment_count === 1 ? "" : "s"}` : ""
        }.`
      );
      closeEmailAppointmentModal(true);
    } catch (err) {
      console.error(err);
      setEmailAppointmentError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to send future appointments email."
      );
    } finally {
      setEmailAppointmentSending(false);
    }
  }, [
    closeEmailAppointmentModal,
    currentClientEmail,
    emailAppointmentValue,
    futureAppointments,
    initialClient,
    validateEmailAddress,
  ]);

  const isSameLocalDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const serviceHistory = useMemo(() => {
    const now = new Date();
    return (serviceLog || [])
      .map((entry) => ({
        ...entry,
        _date: entry?.date ? new Date(entry.date) : null,
      }))
      .filter(
        (entry) => entry._date && (entry._date < now || isSameLocalDay(entry._date, now))
      )
      .sort((a, b) => b._date - a._date);
  }, [serviceLog]);

  const showRefillReminderSection = true;

  const lastQualifyingServiceDateLabel = useMemo(() => {
    const rawDate = String(refillLastServiceDate || "").trim();
    if (!rawDate) return "";
    const match = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return rawDate;
    const isoDate = `${match[1]}-${match[2]}-${match[3]}T12:00:00Z`;
    const label = formatInTimeZone(
      isoDate,
      salonTimezone,
      { month: "short", day: "numeric", year: "numeric" },
      "en-US"
    );
    return label && label !== "-" ? label : rawDate;
  }, [refillLastServiceDate, salonTimezone]);

  const nextReminderLabel = useMemo(() => {
    if (!refillNextSendAtUtc) return "";
    const label = formatInTimeZone(
      refillNextSendAtUtc,
      salonTimezone,
      {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      },
      "en-US"
    );
    return label && label !== "-" ? label : "";
  }, [refillNextSendAtUtc, salonTimezone]);

  const resetFilterValues = useCallback(() => {
    setFilterText("");
    setFilterApptType("all");
    setMinPrice("");
    setMaxPrice("");
    setStartDate("");
    setEndDate("");
  }, []);

  useEffect(() => {
    resetFilterValues();
  }, [filterBy, resetFilterValues]);

  const filteredServiceHistory = useMemo(() => {
    const rows = Array.isArray(serviceHistory) ? serviceHistory : [];
    const norm = (value) => String(value || "").toLowerCase().trim();
    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const parseDateValue = (value) => {
      if (!value) return null;
      const isoTry = new Date(value);
      if (!Number.isNaN(isoTry.getTime())) return isoTry;
      const match = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (match) {
        return new Date(
          Number(match[3]),
          Number(match[1]) - 1,
          Number(match[2])
        );
      }
      return null;
    };

    return rows.filter((entry) => {
      const service = entry.service_name || entry.service || "";
      const apptType = entry.appointment_type || entry.client_type || "";
      const provider =
        entry.provider_name || entry.provider || entry.staff_name || "";
      const price = toNumber(entry.price);
      const dateObj = parseDateValue(entry.date);

      if (filterBy === "service") {
        return norm(service).includes(norm(filterText));
      }

      if (filterBy === "provider") {
        return norm(provider).includes(norm(filterText));
      }

      if (filterBy === "appointment_type") {
        if (filterApptType === "all") return true;
        return String(apptType) === String(filterApptType);
      }

      if (filterBy === "price") {
        const minValue = minPrice === "" ? null : toNumber(minPrice);
        const maxValue = maxPrice === "" ? null : toNumber(maxPrice);
        if (price == null) return false;
        if (minValue != null && price < minValue) return false;
        if (maxValue != null && price > maxValue) return false;
        return true;
      }

      if (filterBy === "date") {
        if (!dateObj) return false;
        const startValue = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const endValue = endDate ? new Date(`${endDate}T23:59:59`) : null;
        if (startValue && dateObj < startValue) return false;
        if (endValue && dateObj > endValue) return false;
        return true;
      }

      return true;
    });
  }, [
    serviceHistory,
    filterBy,
    filterText,
    filterApptType,
    minPrice,
    maxPrice,
    startDate,
    endDate,
  ]);

  const filteredTicketRows = useMemo(() => {
    const rows = Array.isArray(ticketRows) ? ticketRows : [];
    const term = ticketSearch.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const ticketNumber = String(row.ticket_number || row.ticket_id || "").toLowerCase();
      const typeLabel = String(row.type_label || "").toLowerCase();
      const typeValue = String(row.type || "").toLowerCase();
      return (
        ticketNumber.includes(term) ||
        typeLabel.includes(term) ||
        typeValue.includes(term)
      );
    });
  }, [ticketRows, ticketSearch]);

  const reviewByTicketId = useMemo(() => {
    const map = {};
    (Array.isArray(reviewRows) ? reviewRows : []).forEach((row) => {
      if (!row?.ticket_id) return;
      map[row.ticket_id] = row;
    });
    return map;
  }, [reviewRows]);

  const toggleReviewExpanded = (ticketId) => {
    if (!ticketId) return;
    setReviewExpanded((prev) => ({
      ...prev,
      [ticketId]: !prev?.[ticketId],
    }));
  };

  const openReviewDetail = (review) => {
    if (!review) return;
    setReviewDetail(review);
    setReviewDetailOpen(true);
  };

  const closeReviewDetail = () => {
    setReviewDetailOpen(false);
    setReviewDetail(null);
  };

  const handleViewTicket = (ticketId) => {
    if (!ticketId) return;
    onClose?.();
    navigate(`/tickets/${ticketId}`);
  };

  const handleOpenNoteEditor = () => {
    setNoteDraft("");
    setNoteEditorOpen(true);
    setNotesError("");
  };

  const handleCloseNoteEditor = () => {
    setNoteEditorOpen(false);
    setNoteDraft("");
  };

  const handleSaveNote = async () => {
    if (!hasClientId || !noteDraft.trim()) return;
    setNoteSaving(true);
    setNotesError("");
    try {
      await createClientNote(initialClient.id, {
        note: noteDraft.trim(),
        note_type: "manual",
      });
      notesPanelRef.current?.refresh();
      handleCloseNoteEditor();
    } catch (err) {
      console.error(err);
      setNotesError("Unable to save note. Please try again.");
    } finally {
      setNoteSaving(false);
    }
  };

  const loadHealthConcernNotes = useCallback(async (clientId) => {
    if (!clientId) {
      setHealthConcernNotes([]);
      setHealthConcernNotesError("");
      return;
    }
    setHealthConcernNotesLoading(true);
    setHealthConcernNotesError("");
    try {
      const response = await listClientNotes(clientId, {
        note_type: `${HEALTH_CONCERN_NOTE_TYPE},${PORTAL_HEALTH_CONCERN_NOTE_TYPE}`,
      });
      const rows = Array.isArray(response?.notes) ? response.notes : [];
      const sortedRows = [...rows].sort((a, b) => {
        const left = new Date(a?.created_at || 0).getTime();
        const right = new Date(b?.created_at || 0).getTime();
        return right - left;
      });
      setHealthConcernNotes(sortedRows);
    } catch (err) {
      console.error(err);
      setHealthConcernNotesError("Unable to load health concern notes.");
      setHealthConcernNotes([]);
    } finally {
      setHealthConcernNotesLoading(false);
    }
  }, []);

  const handleAddHealthConcernNote = async () => {
    const text = (healthConcernNoteText || "").trim();
    if (!text) return;
    if (!healthConcernClientId) {
      setHealthConcernNotesError("Save the client first, then add a health concern note.");
      return;
    }
    setHealthConcernNotesSaving(true);
    setHealthConcernNotesError("");
    try {
      await createClientNote(healthConcernClientId, {
        note: text,
        note_type: HEALTH_CONCERN_NOTE_TYPE,
      });
      setHealthConcernNoteText("");
      await loadHealthConcernNotes(healthConcernClientId);
    } catch (err) {
      console.error(err);
      setHealthConcernNotesError("Unable to save health concern note.");
    } finally {
      setHealthConcernNotesSaving(false);
    }
  };

  const showHouseLockedToast = useCallback(() => {
    if (!isHouseAccount) return;
    const now = Date.now();
    if (now - lastHouseToastAt.current < 3000) return;
    lastHouseToastAt.current = now;
    setHouseToast(houseAccountMessage);
    if (houseToastTimer.current) {
      clearTimeout(houseToastTimer.current);
    }
    houseToastTimer.current = setTimeout(() => {
      setHouseToast("");
    }, 3000);
  }, [houseAccountMessage, isHouseAccount]);

  useEffect(() => {
    return () => {
      if (houseToastTimer.current) {
        clearTimeout(houseToastTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setResolvedClient(initialClient || null);
      setLoadingClientRecord(false);
      return;
    }
    if (!initialClient?.id) {
      setResolvedClient(initialClient || null);
      setLoadingClientRecord(false);
      return;
    }
    let cancelled = false;
    setResolvedClient(initialClient);
    setLoadingClientRecord(true);
    (async () => {
      try {
        const latest = await getClientById(initialClient.id);
        if (!cancelled && latest) {
          setResolvedClient(latest);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setResolvedClient(initialClient);
        }
      } finally {
        if (!cancelled) {
          setLoadingClientRecord(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialClient, initialClient?.id, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      formSeededForOpenRef.current = false;
      return;
    }
    if (!isEdit && formSeededForOpenRef.current) {
      return;
    }
    if (isEdit) {
      if ((initialSection || "").trim().toLowerCase() === "health_safety") {
        setActiveTab(TAB_PROFILE);
      } else {
        setActiveTab(resolveInitialTab(initialTab));
      }
    } else {
      setActiveTab(TAB_PROFILE);
    }
    setError("");
    setFieldErrors({});
    setRequiredSubmitNotice(false);

    const baseSalonId =
      hydratedClient?.salon_id ||
      preferredSalonId ||
      (salons && salons.length === 1 ? salons[0].id : null);

    if (hydratedClient) {
      const { firstName: f, lastName: l } = splitName(hydratedClient.full_name);
      const parsed = parseNotes(hydratedClient.notes);
      setFirstName(f);
      setLastName(l);
      setEmail(hydratedClient.email || "");
      setPhone(
        showPhoneField ? toNANPDigits10(hydratedClient.phone || "") : ""
      );
      setPreferredStaffId(
        hydratedClient?.preferred_staff_id != null
          ? String(hydratedClient.preferred_staff_id)
          : ""
      );
      setClientLabel(hydratedClient.client_label || "");
      setPreferredContact(parsed.preferredContact || "");
      setReceiveReminders(!!parsed.receiveReminders);
      setReceiveMarketing(!!parsed.receiveMarketing);
      setNotes(parsed.notesText || "");
      setIsDiabetic(!!hydratedClient.is_diabetic);
      setDiabeticFlaggedAt(hydratedClient.is_diabetic_flagged_at || null);
      setHasHealthConcerns(!!hydratedClient.has_health_concerns);
      setHealthConcernsFlaggedAt(
        hydratedClient.health_concerns_flagged_at || null
      );
      setHealthConcernNoteText("");
      setHealthConcernNotes([]);
      setHealthConcernNotesLoading(false);
      setHealthConcernNotesError("");
      setHealthConcernNotesSaving(false);
      setSalonId(baseSalonId);
      setAddress1(hydratedClient.address1 || "");
      setAddress2(hydratedClient.address2 || "");
      setCity(hydratedClient.city || "");
      setStateProvince(hydratedClient.state_province || "");
      setPostalCode(hydratedClient.postal_code || "");
      setMarried(
        hydratedClient.married === true
          ? "true"
          : hydratedClient.married === false
          ? "false"
          : ""
      );
      setGenderPronouns(hydratedClient.gender_pronouns || "");
      setBirthday(normalizeDateInput(hydratedClient.birthday));
      setOcsAccountLocked(!!hydratedClient.ocs_account_locked);
      setOcsUsername(hydratedClient.ocs_username || "");
      setOcsPassword("");
      setRefillReminderEnabled(!!hydratedClient.refill_reminder_enabled);
      setRefillIntervalWeeks(
        Number(hydratedClient.refill_interval_weeks || 3) || 3
      );
      setRefillLastServiceDate(hydratedClient.refill_last_service_date || null);
      setRefillNextSendAtUtc(hydratedClient.refill_next_send_at_utc || null);
    } else {
      const prefillName = prefill?.full_name || prefill?.name || "";
      const { firstName: prefillFirst, lastName: prefillLast } =
        splitName(prefillName);
      setFirstName(prefillFirst);
      setLastName(prefillLast);
      setEmail(prefill?.email || "");
      setPhone(
        showPhoneField ? toNANPDigits10(prefill?.phone || "") : ""
      );
      setPreferredStaffId("");
      setClientLabel("");
      setPreferredContact("");
      setReceiveReminders(false);
      setReceiveMarketing(false);
      setNotes("");
      setIsDiabetic(false);
      setDiabeticFlaggedAt(null);
      setHasHealthConcerns(false);
      setHealthConcernsFlaggedAt(null);
      setHealthConcernNoteText("");
      setHealthConcernNotes([]);
      setHealthConcernNotesLoading(false);
      setHealthConcernNotesError("");
      setHealthConcernNotesSaving(false);
      setSalonId(baseSalonId);
      setAddress1("");
      setAddress2("");
      setCity("");
      setStateProvince("");
      setPostalCode("");
      setMarried("");
      setGenderPronouns("");
      setBirthday("");
      setOcsAccountLocked(false);
      setOcsUsername("");
      setOcsPassword("");
      setRefillReminderEnabled(false);
      setRefillIntervalWeeks(3);
      setRefillLastServiceDate(null);
      setRefillNextSendAtUtc(null);
    }
    formSeededForOpenRef.current = true;
  }, [
    hydratedClient,
    isOpen,
    salons,
    preferredSalonId,
    isEdit,
    showPhoneField,
    initialTab,
    initialSection,
    prefill,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab !== TAB_PROFILE) return;
    if ((initialSection || "").trim().toLowerCase() !== "health_safety") return;
    const sectionEl = healthSafetySectionRef.current;
    if (!sectionEl) return;
    sectionEl.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [activeTab, initialSection, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setHealthConcernNotes([]);
      setHealthConcernNotesError("");
      setHealthConcernNotesLoading(false);
      return;
    }
    if (!hasHealthConcerns) {
      setHealthConcernNotes([]);
      setHealthConcernNotesError("");
      setHealthConcernNotesLoading(false);
      return;
    }
    if (!healthConcernClientId) {
      setHealthConcernNotes([]);
      setHealthConcernNotesError("");
      setHealthConcernNotesLoading(false);
      return;
    }
    loadHealthConcernNotes(healthConcernClientId);
  }, [
    hasHealthConcerns,
    healthConcernClientId,
    isOpen,
    loadHealthConcernNotes,
  ]);

  const loadServiceLog = useCallback(async () => {
    if (!initialClient?.id) return;
    const clientId = initialClient.id;
    setServiceLogLoading(true);
    setServiceLogError("");
    try {
      const logEntries = await getClientServiceLog(clientId);
      setServiceLog(Array.isArray(logEntries) ? logEntries : []);
    } catch (err) {
      const message = err?.message || "Unable to load client details.";
      setServiceLogError(message);
    } finally {
      setServiceLogLoading(false);
    }
  }, [initialClient?.id]);

  const loadClientGiftCards = useCallback(async () => {
    if (!initialClient?.id) return;
    const clientId = initialClient.id;
    setClientGiftCardsLoading(true);
    setClientGiftCardsError("");
    try {
      const data = await listClientGiftCards(clientId);
      setClientGiftCards(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setClientGiftCardsError(
        formatPermissionError(err, "Unable to load gift cards right now.")
      );
      setClientGiftCards([]);
    } finally {
      setClientGiftCardsLoading(false);
    }
  }, [initialClient?.id]);

  useEffect(() => {
    if (!isOpen) {
      setPreferredStaffOptions([]);
      setPreferredStaffLoading(false);
      return;
    }
    if (!effectiveSalonId) {
      setPreferredStaffOptions([]);
      setPreferredStaffLoading(false);
      return;
    }
    let cancelled = false;
    setPreferredStaffLoading(true);
    (async () => {
      try {
        const rows = await listStaff({ salon_id: effectiveSalonId });
        const selectedId =
          hydratedClient?.preferred_staff_id != null
            ? Number(hydratedClient.preferred_staff_id)
            : null;
        const keepSelected = (staffMember) =>
          selectedId != null && Number(staffMember?.id) === selectedId;
        const filtered = (Array.isArray(rows) ? rows : []).filter((staffMember) => {
          if (!staffMember || !staffMember.id) return false;
          const isActive = staffMember.is_active !== false;
          if (keepSelected(staffMember)) return true;
          if (!isActive) return false;
          if (staffMember.perform_services !== undefined) {
            return Boolean(staffMember.perform_services);
          }
          return String(staffMember.role || "").toLowerCase() === "technician";
        });
        filtered.sort((a, b) => {
          const orderA =
            a?.display_order === null || a?.display_order === undefined
              ? Number.POSITIVE_INFINITY
              : Number(a.display_order);
          const orderB =
            b?.display_order === null || b?.display_order === undefined
              ? Number.POSITIVE_INFINITY
              : Number(b.display_order);
          if (orderA !== orderB) return orderA - orderB;
          return String(a?.display_name || "").localeCompare(
            String(b?.display_name || "")
          );
        });
        if (!cancelled) {
          setPreferredStaffOptions(filtered);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setPreferredStaffOptions([]);
        }
      } finally {
        if (!cancelled) {
          setPreferredStaffLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveSalonId, hydratedClient?.preferred_staff_id, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) {
      setServiceLog([]);
      setFutureAppts([]);
      setServiceLogError("");
      setFutureApptsError("");
      setFutureApptsLoading(false);
      setClientGiftCards([]);
      setClientGiftCardsError("");
      setClientGiftCardsLoading(false);
      setTicketRows([]);
      setTicketRowsError("");
      setTicketRowsLoading(false);
      setReviewRows([]);
      setReviewRowsError("");
      setReviewRowsLoading(false);
      setReviewExpanded({});
      setTicketSearch("");
      setTicketStartDate(today);
      setTicketEndDate(today);
      setRewardsData(null);
      setRewardsTransactions([]);
      setRewardsSettings(null);
      setRewardsLoading(false);
      setRewardsError("");
      setReferralInput("");
      setReferralLookup(null);
      setReferralLookupError("");
      setReferralBlockInfo(null);
      setReferralNotice("");
      setReferralLinking(false);
      setCopyNotice("");
      setNoteEditorOpen(false);
      setNoteDraft("");
      setNotesError("");
      return;
    }
    loadServiceLog();
  }, [isOpen, initialClient?.id, loadServiceLog, today]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    if (activeTab !== TAB_GIFT_CARDS) return;
    loadClientGiftCards();
  }, [activeTab, isOpen, initialClient?.id, loadClientGiftCards]);

  const loadClientTickets = useCallback(async () => {
    if (!initialClient?.id) return;
    const resolvedSalonId =
      initialClient?.salon_id || preferredSalonId || effectiveSalonId;
    setTicketRowsLoading(true);
    setTicketRowsError("");
    try {
      const data = await getClientTickets(initialClient.id, {
        salon_id: resolvedSalonId,
        start: ticketStartDate || undefined,
        end: ticketEndDate || undefined,
      });
      setTicketRows(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err?.message || "Unable to load tickets.";
      setTicketRowsError(message);
      setTicketRows([]);
    } finally {
      setTicketRowsLoading(false);
    }
  }, [
    initialClient?.id,
    initialClient?.salon_id,
    preferredSalonId,
    effectiveSalonId,
    ticketStartDate,
    ticketEndDate,
  ]);

  const loadClientReviews = useCallback(async () => {
    if (!initialClient?.id) return;
    setReviewRowsLoading(true);
    setReviewRowsError("");
    try {
      const data = await getClientServiceLogReviews(initialClient.id, {
        start_date: ticketStartDate || undefined,
        end_date: ticketEndDate || undefined,
      });
      setReviewRows(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err?.response?.data?.detail || "Unable to load reviews.";
      setReviewRowsError(message);
      setReviewRows([]);
    } finally {
      setReviewRowsLoading(false);
    }
  }, [initialClient?.id, ticketStartDate, ticketEndDate]);

  const loadEmailMessages = useCallback(async () => {
    if (!initialClient?.id) return;
    setEmailMessagesLoading(true);
    setEmailMessagesError("");
    try {
      const data = await listClientEmailMessages(initialClient.id, { limit: 75 });
      setEmailMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err?.response?.data?.detail || "Unable to load email messages.";
      setEmailMessagesError(message);
      setEmailMessages([]);
    } finally {
      setEmailMessagesLoading(false);
    }
  }, [initialClient?.id]);

  const loadEmailPreferences = useCallback(async () => {
    if (!initialClient?.id) return;
    setEmailPreferencesLoading(true);
    setEmailPreferencesError("");
    try {
      const data = await listClientEmailPreferences(initialClient.id);
      setEmailPreferences(Array.isArray(data) ? data : []);
    } catch (err) {
      const message =
        err?.response?.data?.detail || "Unable to load email preferences.";
      setEmailPreferencesError(message);
      setEmailPreferences([]);
    } finally {
      setEmailPreferencesLoading(false);
    }
  }, [initialClient?.id]);

  const handleEmailPreferenceToggle = useCallback(
    async (preference) => {
      if (!initialClient?.id || !preference?.category || !canEditClient) return;
      const category = preference.category;
      const nextValue = !preference.is_subscribed;
      setEmailPreferencesSaving(category);
      setEmailPreferencesError("");
      try {
        const data = await updateClientEmailPreference(initialClient.id, {
          category,
          is_subscribed: nextValue,
          reason: nextValue ? "Salon manually resubscribed" : "Salon manually unsubscribed",
        });
        setEmailPreferences(Array.isArray(data?.preferences) ? data.preferences : []);
      } catch (err) {
        const message =
          err?.response?.data?.detail || "Unable to update email preference.";
        setEmailPreferencesError(message);
      } finally {
        setEmailPreferencesSaving("");
      }
    },
    [canEditClient, initialClient?.id]
  );

  const handleCopyEmailPreferenceLink = useCallback(async () => {
    if (!initialClient?.id || !effectiveSalonId) return;
    setEmailPreferenceLinkLoading(true);
    setEmailPreferencesError("");
    try {
      const data = await getClientEmailPreferenceLink({
        salonId: effectiveSalonId,
        clientId: initialClient.id,
        token,
        category: "all_optional",
      });
      const link = data?.url || `${window.location.origin}${data?.path || ""}`;
      if (!link) {
        throw new Error("Missing unsubscribe link");
      }
      await navigator.clipboard.writeText(link);
      setCopyNotice("Unsubscribe link copied.");
      setTimeout(() => setCopyNotice(""), 2000);
    } catch (err) {
      const message =
        err?.response?.data?.detail || "Unable to copy unsubscribe link.";
      setEmailPreferencesError(message);
    } finally {
      setEmailPreferenceLinkLoading(false);
    }
  }, [effectiveSalonId, initialClient?.id, token]);

  const loadRewards = useCallback(async () => {
    if (!initialClient?.id) return;
    setRewardsLoading(true);
    setRewardsError("");
    setReferralLookup(null);
    setReferralLookupError("");
    setReferralBlockInfo(null);
    setReferralNotice("");
    try {
      const [settings, account, transactions] = await Promise.all([
        getRewardsSettings(),
        getClientRewards(initialClient.id),
        getClientRewardTransactions(initialClient.id, { limit: 10, offset: 0 }),
      ]);
      setRewardsSettings(settings);
      setRewardsData(account);
      setRewardsTransactions(Array.isArray(transactions) ? transactions : []);
    } catch (err) {
      setRewardsError(
        formatPermissionError(err, "Unable to load rewards right now.")
      );
      setRewardsData(null);
      setRewardsTransactions([]);
      setRewardsSettings(null);
    } finally {
      setRewardsLoading(false);
    }
  }, [initialClient?.id]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    if (activeTab !== TAB_FUTURE_APPTS) return;
    setFutureApptsNotice("");
    fetchFutureAppointments(initialClient.id);
  }, [activeTab, fetchFutureAppointments, initialClient?.id, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    if (activeTab !== TAB_TICKETS) return;
    loadClientTickets();
    loadClientReviews();
  }, [activeTab, loadClientTickets, loadClientReviews, initialClient?.id, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    if (activeTab !== TAB_REWARDS) return;
    loadRewards();
  }, [activeTab, loadRewards, initialClient?.id, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    if (activeTab !== TAB_EMAILS) return;
    loadEmailMessages();
    loadEmailPreferences();
  }, [activeTab, loadEmailMessages, loadEmailPreferences, initialClient?.id, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    const handleAppointmentsChanged = () => {
      fetchFutureAppointments(initialClient.id);
    };
    window.addEventListener(
      APPOINTMENTS_CHANGED_EVENT,
      handleAppointmentsChanged
    );
    return () => {
      window.removeEventListener(
        APPOINTMENTS_CHANGED_EVENT,
        handleAppointmentsChanged
      );
    };
  }, [fetchFutureAppointments, initialClient?.id, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialClient?.id) return;
    const handleServiceLogChanged = (event) => {
      const targetClientId = event?.detail?.clientId;
      if (targetClientId && String(targetClientId) !== String(initialClient.id)) {
        return;
      }
      loadServiceLog();
    };
    window.addEventListener(
      CLIENT_SERVICE_LOG_CHANGED_EVENT,
      handleServiceLogChanged
    );
    return () => {
      window.removeEventListener(
        CLIENT_SERVICE_LOG_CHANGED_EVENT,
        handleServiceLogChanged
      );
    };
  }, [initialClient?.id, isOpen, loadServiceLog]);

  const handleSave = async () => {
    if (!canEditClient) {
      if (isHouseAccount) {
        showHouseLockedToast();
        return;
      }
      onClose?.();
      return;
    }
    setError("");
    setFieldErrors({});
    const full_name = `${firstName} ${lastName}`.trim();
    const nextErrors = {};
    if (!firstName.trim()) {
      nextErrors.first_name = "First name is required.";
    }
    if (!lastName.trim()) {
      nextErrors.last_name = "Last name is required.";
    }
    if (!phone) {
      nextErrors.phone = "Mobile phone is required.";
    } else if (!isValidNANP(phone)) {
      nextErrors.phone = "Mobile phone must be 10 digits.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      if (nextErrors.phone === "Mobile phone must be 10 digits.") {
        setError("Mobile phone must be 10 digits.");
      } else {
        setError("Please fill out First Name, Last Name, and Mobile Phone.");
      }
      setRequiredSubmitNotice(true);
      return;
    }
    if (requireContactInfo) {
      if (!phone) {
        setError("First Name, Last Name, and Mobile Phone are required.");
        return;
      }
      if (!isValidNANP(phone)) {
        setError("Mobile phone must be 10 digits.");
        return;
      }
    }
    const lines = [
      "[Preferences]",
      `Preferred contact: ${preferredContact || "Not set"}`,
      `Appointment reminders: ${receiveReminders ? "Yes" : "No"}`,
      `Marketing: ${receiveMarketing ? "Yes" : "No"}`,
      "",
      "[Notes]",
      notes || "",
    ];
    const payload = {
      full_name,
      salon_id: effectiveSalonId || null,
      preferred_staff_id: preferredStaffId ? Number(preferredStaffId) : null,
      client_label: clientLabel || null,
      is_diabetic: !!isDiabetic,
      has_health_concerns: !!hasHealthConcerns,
      notes: lines.join("\n").trim(),
      address1: address1 || null,
      address2: address2 || null,
      city: city || null,
      state_province: stateProvince || null,
      postal_code: postalCode || null,
      married:
        married === "true" ? true : married === "false" ? false : null,
      gender_pronouns: genderPronouns || null,
      birthday: birthday || null,
      ocs_account_locked: !!ocsAccountLocked,
      ocs_username: ocsUsername || null,
      refill_reminder_enabled: !!refillReminderEnabled,
      refill_interval_weeks: Number(refillIntervalWeeks) || 3,
    };
    const trimmedEmail = (email || "").trim();
    payload.email = trimmedEmail ? trimmedEmail : null;
    payload.phone = phone ? phone : null;
    if (ocsPassword && ocsPassword.trim()) {
      payload.ocs_password = ocsPassword.trim();
    }
    try {
      setSaving(true);
      const saved = await onSave?.(payload);
      notesPanelRef.current?.refresh();
      if (!isEdit) {
        onCreated?.(saved || null);
      }
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(
        err?.message ||
          "Unable to save client. Please check your permissions and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const referralBlockedLabel = useMemo(() => {
    if (!referralBlockInfo?.ticketNumber || !referralBlockInfo?.closedAtUtc) {
      return "";
    }
    const dateLabel = formatInTimeZone(
      referralBlockInfo.closedAtUtc,
      salonTimezone,
      { month: "short", day: "numeric", year: "numeric" },
      "en-US"
    );
    return `First closed ticket: ${referralBlockInfo.ticketNumber} on ${dateLabel}`;
  }, [referralBlockInfo, salonTimezone]);

  const formatRewardTxLabel = (txType) => {
    const raw = String(txType || "");
    const normalized = raw.toLowerCase();
    if (normalized === "referral_bonus") return "Referral Bonus";
    if (normalized === "referral_bonus_reversal") return "Referral Bonus Reversal";
    return raw.replaceAll("_", " ");
  };

  const visibleTabs = useMemo(
    () =>
      isHealthSafetyOnlyView
        ? [{ key: TAB_PROFILE, label: "Health & Safety" }]
        : [
            { key: TAB_PROFILE, label: "Profile" },
            ...(hasClientId
              ? [
                  { key: TAB_FUTURE_APPTS, label: "Future Appts" },
                  { key: TAB_SERVICES, label: "Services" },
                  { key: TAB_TICKETS, label: "Tickets" },
                  { key: TAB_GIFT_CARDS, label: "Gift Cards" },
                  { key: TAB_REWARDS, label: "Rewards" },
                  { key: TAB_EMAILS, label: "Emails" },
                  { key: TAB_NOTES, label: "Notes" },
                ]
              : []),
          ],
    [hasClientId, isHealthSafetyOnlyView]
  );

  if (!isOpen) return null;

  const inputDisabled = !canEditClient;
  const createIsValid =
    isEdit ||
    (firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      isValidNANP(phone));
  const contactInfoMessage = requireContactInfo
    ? "First Name, Last Name, and Mobile Phone are required."
    : "";
  const isReadOnlyTab =
    isEdit &&
    [
      TAB_FUTURE_APPTS,
      TAB_SERVICES,
      TAB_TICKETS,
      TAB_GIFT_CARDS,
      TAB_EMAILS,
      TAB_NOTES,
      TAB_REWARDS,
    ].includes(activeTab);
  const resolvedFullName =
    initialClient?.full_name?.trim() ||
    `${firstName} ${lastName}`.trim() ||
    "Unnamed Client";
  const pointValueCents = Number(rewardsSettings?.point_value_cents ?? 1) || 1;
  const rewardsPoints = Number(rewardsData?.points_balance ?? 0) || 0;
  const rewardsValue = (rewardsPoints * pointValueCents) / 100;
  const canLinkReferral = Boolean(
    rewardsData &&
      !rewardsData.referred_by_client_id &&
      !rewardsData.referral_linked_at
  );

  const handleCopyReferralCode = async () => {
    if (!rewardsData?.referral_code) return;
    try {
      await navigator.clipboard.writeText(rewardsData.referral_code);
      setCopyNotice("Referral code copied.");
      setTimeout(() => setCopyNotice(""), 2000);
    } catch (err) {
      setCopyNotice("Unable to copy referral code.");
      setTimeout(() => setCopyNotice(""), 2000);
    }
  };

  const handleLookupReferral = async () => {
    const trimmed = (referralInput || "").trim();
    if (!trimmed) {
      setReferralLookupError("Enter a referral code to look up.");
      return;
    }
    setReferralLookup(null);
    setReferralLookupError("");
    setReferralBlockInfo(null);
    setReferralNotice("");
    try {
      const res = await lookupReferralCode(trimmed);
      setReferralLookup(res);
    } catch (err) {
      setReferralLookupError(
        err?.response?.data?.detail || "Referral code not found."
      );
    }
  };

  const handleLinkReferral = async () => {
    if (!initialClient?.id || !canLinkReferral) return;
    const code = referralLookup?.referral_code || referralInput;
    if (!code) {
      setReferralLookupError("Validate a referral code first.");
      return;
    }
    setReferralLinking(true);
    setReferralLookupError("");
    setReferralBlockInfo(null);
    setReferralNotice("");
    try {
      await linkClientReferral(initialClient.id, { referral_code: code });
      setReferralNotice(
        "Referral linked. Bonus points will be added after the referred client's first paid ticket is closed."
      );
      setReferralInput("");
      setReferralLookup(null);
      setReferralBlockInfo(null);
      await loadRewards();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "REFERRAL_LINK_BLOCKED_FIRST_CLOSED_TICKET") {
          setReferralLookupError(
            "Referral must be linked before the first closed ticket."
          );
          setReferralBlockInfo({
            ticketNumber: detail.first_closed_ticket_number,
            closedAtUtc: detail.first_closed_at_utc,
          });
        } else {
          setReferralLookupError(detail.message || "Unable to link referral.");
        }
      } else {
        setReferralLookupError(detail || "Unable to link referral.");
      }
    } finally {
      setReferralLinking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-start justify-center bg-black/60 px-3 sm:items-center sm:py-4"
      style={{
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
      }}
    >
      <div
        className={`relative w-full max-w-[1080px] min-h-0 rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col max-h-[78vh] sm:max-h-[90vh] ${MODAL_TEXT}`}
        style={{
          maxHeight:
            "min(1080px, calc(78dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)))",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-rose-500 px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0 text-lg sm:text-xl md:text-2xl font-semibold text-white">
            {isEdit ? (
              <span className="block truncate">
                Client Profile{" "}
                <span className="text-white/85 font-medium">
                  - {resolvedFullName}
                </span>
              </span>
            ) : (
              "New Client"
            )}
          </div>
          <ModalCloseX onClick={onClose} />
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-4 pt-2 sm:px-6 sm:pt-3">
          <div className="flex flex-wrap gap-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`${TAB_BASE} ${
                  activeTab === tab.key ? TAB_ACTIVE : TAB_INACTIVE
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          {error && (
            <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          {isHouseAccount && (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {houseAccountMessage}
            </div>
          )}
          {houseToast && (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {houseToast}
            </div>
          )}

          {activeTab === TAB_PROFILE && (
            <div
              className={`space-y-8 ${isHouseAccount ? "house-account-locked" : ""}`}
              onClick={isHouseAccount ? showHouseLockedToast : undefined}
            >
              {loadingClientRecord && isEdit && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500">
                  Refreshing latest client settings...
                </div>
              )}
              {!isHealthSafetyOnlyView && (
                <section>
                {isEdit && canViewClientMetrics && (
                  <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-center ring-1 ring-slate-100">
                      <div className="text-[11px] font-semibold text-slate-500">
                        Visits
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        {Number.isFinite(clientMetrics.visits) ? clientMetrics.visits : 0}
                      </div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-center ring-1 ring-slate-100">
                      <div className="text-[11px] font-semibold text-slate-500">
                        Spent
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        {formatClientSpent(clientMetrics.spent)}
                      </div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-center ring-1 ring-slate-100">
                      <div className="text-[11px] font-semibold text-slate-500">
                        Last
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        {formatLastVisitAge(clientMetrics.lastVisitDate)}
                      </div>
                    </div>
                  </div>
                )}
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Basic Information
                </h3>
                {!isEdit && (
                  <p className="mb-3 text-xs text-slate-500">
                    * First Name, Last Name, and Mobile Phone are required to create a
                    client.
                  </p>
                )}
                {!isEdit && requiredSubmitNotice && !createIsValid && (
                  <p className="mb-3 text-xs text-rose-600">
                    Please fill out First Name, Last Name, and Mobile Phone to create the
                    client.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      First Name
                      <span className="text-rose-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, first_name: "" }));
                        setRequiredSubmitNotice(false);
                      }}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                    {fieldErrors.first_name && (
                      <p className="text-xs text-rose-600">{fieldErrors.first_name}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Last Name
                      <span className="text-rose-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, last_name: "" }));
                        setRequiredSubmitNotice(false);
                      }}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                    {fieldErrors.last_name && (
                      <p className="text-xs text-rose-600">{fieldErrors.last_name}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                  {showPhoneField && (
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>
                        Mobile Phone
                        <span className="text-rose-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        value={formatNANP(phone)}
                        onChange={(e) => {
                          setPhone(toNANPDigits10(e.target.value));
                          setFieldErrors((prev) => ({ ...prev, phone: "" }));
                          setRequiredSubmitNotice(false);
                        }}
                        className={`w-full ${FIELD_CLASS}`}
                        disabled={inputDisabled}
                      />
                      {fieldErrors.phone && (
                        <p className="text-xs text-rose-600">{fieldErrors.phone}</p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>Preferred Technician</label>
                    <select
                      value={preferredStaffId}
                      onChange={(e) => setPreferredStaffId(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled || preferredStaffLoading}
                    >
                      <option value="">
                        {preferredStaffLoading ? "Loading..." : "Select..."}
                      </option>
                      {preferredStaffOptions.map((staffMember) => (
                        <option key={staffMember.id} value={String(staffMember.id)}>
                          {staffMember.display_name || `Staff #${staffMember.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>Client Label</label>
                    <select
                      value={clientLabel}
                      onChange={(e) => setClientLabel(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    >
                      <option value="">Select...</option>
                      {CLIENT_LABEL_OPTIONS.map((optionValue) => (
                        <option key={optionValue} value={optionValue}>
                          {optionValue}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {requireContactInfo && (
                  <p className="mt-2 text-xs text-rose-600">
                    {contactInfoMessage}
                  </p>
                )}
                </section>
              )}

              <section ref={healthSafetySectionRef}>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Health &amp; Safety
                </h3>
                {isHealthSafetyOnlyView && (
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                      Client
                    </div>
                    <div className="mt-1 text-base font-semibold text-slate-900">
                      {resolvedFullName}
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                      checked={isDiabetic}
                      onChange={(e) => {
                        const nextValue = e.target.checked;
                        setIsDiabetic(nextValue);
                        if (!nextValue) {
                          setDiabeticFlaggedAt(null);
                        }
                      }}
                      disabled={inputDisabled}
                    />
                    Diabetic (requires special attention)
                  </label>
                  {isDiabetic && (
                    <p className="pl-6 text-xs text-slate-500">
                      Flagged on: {diabeticFlaggedDateLabel || "Pending save"}
                    </p>
                  )}

                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                      checked={hasHealthConcerns}
                      onChange={(e) => {
                        const nextValue = e.target.checked;
                        setHasHealthConcerns(nextValue);
                        if (!nextValue) {
                          setHealthConcernsFlaggedAt(null);
                          setHealthConcernNoteText("");
                          setHealthConcernNotes([]);
                          setHealthConcernNotesError("");
                          setHealthConcernNotesLoading(false);
                        }
                      }}
                      disabled={inputDisabled}
                    />
                    Health concerns
                  </label>
                  {hasHealthConcerns && (
                    <div className="ml-6 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="space-y-1">
                        <label className={LABEL_TEXT}>Health concern notes</label>
                        {healthConcernsFlaggedDateLabel && (
                          <p className="text-xs text-slate-500">
                            Flagged on: {healthConcernsFlaggedDateLabel}
                          </p>
                        )}
                      </div>
                      <textarea
                        value={healthConcernNoteText}
                        onChange={(e) => setHealthConcernNoteText(e.target.value)}
                        rows={3}
                        className={`w-full ${FIELD_CLASS}`}
                        disabled={inputDisabled || healthConcernNotesSaving}
                        placeholder="Add health concern details..."
                      />
                      <div className="flex items-center justify-between gap-2">
                        {!healthConcernClientId && (
                          <p className="text-xs text-slate-500">
                            Save client first, then add notes.
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={handleAddHealthConcernNote}
                          className={BTN_PRIMARY}
                          disabled={
                            inputDisabled ||
                            healthConcernNotesSaving ||
                            !healthConcernNoteText.trim() ||
                            !healthConcernClientId
                          }
                        >
                          {healthConcernNotesSaving ? "Saving..." : "Add Note"}
                        </button>
                      </div>

                      {healthConcernNotesError && (
                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                          {healthConcernNotesError}
                        </div>
                      )}
                      {healthConcernNotesLoading ? (
                        <div className="text-sm text-slate-500">
                          Loading health concern notes...
                        </div>
                      ) : healthConcernNotes.length === 0 ? (
                        <div className="text-sm text-slate-500">
                          No health concern notes yet.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {healthConcernNotes.map((entry) => {
                            const dateLabel = formatInTimeZone(
                              entry?.created_at,
                              salonTimezone,
                              { month: "short", day: "numeric", year: "numeric" },
                              "en-US"
                            );
                            const who = entry?.created_by_name || "Unknown";
                            const isPortalNote =
                              String(entry?.note_type || "").toUpperCase() ===
                              PORTAL_HEALTH_CONCERN_NOTE_TYPE;
                            return (
                              <div
                                key={entry.id}
                                className="rounded-xl border border-slate-200 bg-white px-3 py-2"
                              >
                                {isPortalNote && (
                                  <div className="mb-2 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                                    Client portal
                                  </div>
                                )}
                                <div className="text-sm text-slate-700 whitespace-pre-line">
                                  {entry.note}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                  {dateLabel && dateLabel !== "-" ? dateLabel : "--"} by {who}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {isEdit && (
                  <div className="mt-4 ml-6 rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-sm font-semibold text-slate-800">
                      Service Waiver
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                          Accepted
                        </div>
                        <div className="mt-1 text-sm text-slate-800">
                          {hydratedClient?.service_waiver_accepted ? "Yes" : "No"}
                        </div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                          Accepted on
                        </div>
                        <div className="mt-1 text-sm text-slate-800">
                          {serviceWaiverAcceptedDateLabel || "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {!isHealthSafetyOnlyView && (
                <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1 md:col-span-2">
                    <label className={LABEL_TEXT}>
                      Address 1
                    </label>
                    <input
                      type="text"
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                  <div className="flex flex-col gap-1 md:col-span-2">
                    <label className={LABEL_TEXT}>
                      Address 2
                    </label>
                    <input
                      type="text"
                      value={address2}
                      onChange={(e) => setAddress2(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      City
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      State/Province
                    </label>
                    <select
                      value={stateProvince}
                      onChange={(e) => setStateProvince(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    >
                      {STATE_PROVINCE_OPTIONS.map((option) => (
                        <option key={option || "blank"} value={option}>
                          {option || "Select..."}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                </div>
                </section>
              )}

              {!isHealthSafetyOnlyView && (
                <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Demographics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Married
                    </label>
                    <select
                      value={married}
                      onChange={(e) => setMarried(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Gender/Pronouns
                    </label>
                    <select
                      value={genderPronouns}
                      onChange={(e) => setGenderPronouns(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    >
                      {GENDER_PRONOUN_OPTIONS.map((option) => (
                        <option key={option || "blank"} value={option}>
                          {option || "Select..."}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Birthday
                    </label>
                    <input
                      type="date"
                      value={birthday}
                      onChange={(e) => setBirthday(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                </div>
                </section>
              )}

              {!isHealthSafetyOnlyView && (
                <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Online Client Booking
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Account Locked
                    </label>
                    <select
                      value={ocsAccountLocked ? "true" : "false"}
                      onChange={(e) => setOcsAccountLocked(e.target.value === "true")}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Username
                    </label>
                    <input
                      type="text"
                      value={ocsUsername}
                      onChange={(e) => setOcsUsername(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Password
                    </label>
                    <input
                      type="password"
                      value={ocsPassword}
                      onChange={(e) => setOcsPassword(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                      placeholder={isEdit ? "Leave blank to keep current password" : ""}
                    />
                  </div>
                </div>
                </section>
              )}

              {/* Contact & Communication */}
              <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Contact &amp; Communication
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_TEXT}>
                      Preferred Contact Method
                    </label>
                    <select
                      value={preferredContact}
                      onChange={(e) => setPreferredContact(e.target.value)}
                      className={`w-full ${FIELD_CLASS}`}
                      disabled={inputDisabled}
                    >
                      <option value="">Select...</option>
                      <option value="Email">Email</option>
                      <option value="Phone">Phone</option>
                      <option value="SMS">SMS</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className={LABEL_TEXT}>
                      Preferences
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                        checked={receiveReminders}
                        onChange={(e) => setReceiveReminders(e.target.checked)}
                        disabled={inputDisabled}
                      />
                      Receive appointment reminders
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                        checked={receiveMarketing}
                        onChange={(e) => setReceiveMarketing(e.target.checked)}
                        disabled={inputDisabled}
                      />
                      Receive marketing updates
                    </label>
                  </div>
                </div>
              </section>

              {showRefillReminderSection && (
                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Refill Email Reminders
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border border-slate-300 text-rose-600 focus:ring-0"
                        checked={refillReminderEnabled}
                        onChange={(e) => setRefillReminderEnabled(e.target.checked)}
                        disabled={inputDisabled}
                      />
                      Enable refill email reminders
                    </label>
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_TEXT}>Interval (weeks)</label>
                      <select
                        value={String(refillIntervalWeeks)}
                        onChange={(e) => setRefillIntervalWeeks(Number(e.target.value) || 3)}
                        className={`w-full ${FIELD_CLASS}`}
                        disabled={inputDisabled || !refillReminderEnabled}
                      >
                        <option value="2">2 weeks</option>
                        <option value="3">3 weeks</option>
                        <option value="4">4 weeks</option>
                        <option value="5">5 weeks</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-slate-500">
                    <p>
                      Only clients subscribed to Email Marketing will receive refill
                      reminders.
                    </p>
                    <p>
                      Reminders apply to clients with services containing &quot;full
                      set&quot; or &quot;refill&quot;.
                    </p>
                    {lastQualifyingServiceDateLabel && (
                      <p className="text-slate-600">
                        Last qualifying service date: {lastQualifyingServiceDateLabel}
                      </p>
                    )}
                    {nextReminderLabel && (
                      <p className="text-slate-600">
                        Next reminder scheduled: {nextReminderLabel}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {/* Notes */}
              <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">
                  Client Notes
                </h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={`w-full ${FIELD_CLASS} h-28`}
                  disabled={inputDisabled}
                  placeholder="Internal notes about preferences, past services, etc."
                />
              </section>

            </div>
          )}

          {activeTab === TAB_FUTURE_APPTS && (
            <div className="space-y-4">
              {futureApptsLoading && (
                <div className="text-sm text-slate-500">Loading appointments...</div>
              )}
              {futureApptsError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {futureApptsError}
                </div>
              )}
              {futureApptsNotice && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {futureApptsNotice}
                </div>
              )}
              {!futureApptsLoading && !futureAppointments.length && (
                <div className="text-sm text-slate-500">
                  No future appointments.
                </div>
              )}
              {futureAppointments.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)_56px] gap-3 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Service</span>
                    <span>When</span>
                    <span>With</span>
                    <span>Scheduled By</span>
                    <span className="text-right">Action</span>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {futureAppointments.map((entry) => {
                      const scheduledBy = entry.scheduled_by_name || "--";
                      const isCanceling =
                        cancelingAppointmentId === (entry.appointment_id || entry.id);
                      return (
                        <div
                          key={entry.appointment_id || entry.id}
                          className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)_56px] gap-3 px-4 py-3 text-sm text-slate-700"
                        >
                          <span className="font-semibold text-slate-900">
                            {entry.service_name || "--"}
                          </span>
                          <span>{formatDateTime(entry.start_time)}</span>
                          <span>{entry.with_name || "--"}</span>
                          <span>{scheduledBy}</span>
                          <div className="flex items-center justify-end">
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-lg font-semibold leading-none text-rose-500 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:text-slate-300"
                              onClick={() =>
                                handleCancelFutureAppointment(
                                  entry.appointment_id || entry.id
                                )
                              }
                              disabled={isCanceling}
                              title="Press X to cancel this appointment."
                              aria-label={
                                isCanceling
                                  ? "Cancelling appointment"
                                  : "Cancel appointment. Press X to cancel this appointment."
                              }
                            >
                              {isCanceling ? "…" : "×"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === TAB_SERVICES && (
            <div className="space-y-4">
              {serviceLogLoading && (
                <div className="text-sm text-slate-500">Loading service history...</div>
              )}
              {serviceLogError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {serviceLogError}
                </div>
              )}
              <div className="mb-3 flex flex-wrap items-end gap-3">
                <div className="min-w-[220px]">
                  <div className={LABEL_TEXT}>
                    Filter by
                  </div>
                  <select
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    value={filterBy}
                    onChange={(event) => setFilterBy(event.target.value)}
                  >
                    <option value="service">Service</option>
                    <option value="appointment_type">Appointment Type</option>
                    <option value="provider">Provider</option>
                    <option value="price">Price</option>
                    <option value="date">Date</option>
                  </select>
                </div>

                {(filterBy === "service" || filterBy === "provider") && (
                  <div className="min-w-[280px] flex-1">
                    <div className={LABEL_TEXT}>
                      {filterBy === "service"
                        ? "Service contains"
                        : "Provider contains"}
                    </div>
                    <input
                      className={`mt-1 w-full ${FIELD_CLASS}`}
                      value={filterText}
                      onChange={(event) => setFilterText(event.target.value)}
                      placeholder={
                        filterBy === "service" ? "e.g., Bio Gel" : "e.g., Jenny"
                      }
                    />
                  </div>
                )}

                {filterBy === "appointment_type" && (
                  <div className="min-w-[260px]">
                    <div className={LABEL_TEXT}>
                      Appointment Type
                    </div>
                    <select
                      className={`mt-1 w-full ${FIELD_CLASS}`}
                      value={filterApptType}
                      onChange={(event) => setFilterApptType(event.target.value)}
                    >
                      <option value="all">All appointment types</option>
                      {APPOINTMENT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {filterBy === "price" && (
                  <>
                    <div className="min-w-[160px]">
                      <div className={LABEL_TEXT}>
                        Min price
                      </div>
                      <input
                        type="number"
                        inputMode="decimal"
                        className={`mt-1 w-full ${FIELD_CLASS}`}
                        value={minPrice}
                        onChange={(event) => setMinPrice(event.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="min-w-[160px]">
                      <div className={LABEL_TEXT}>
                        Max price
                      </div>
                      <input
                        type="number"
                        inputMode="decimal"
                        className={`mt-1 w-full ${FIELD_CLASS}`}
                        value={maxPrice}
                        onChange={(event) => setMaxPrice(event.target.value)}
                        placeholder="100"
                      />
                    </div>
                  </>
                )}

                {filterBy === "date" && (
                  <>
                    <div className="min-w-[190px]">
                      <div className={LABEL_TEXT}>
                        From
                      </div>
                      <input
                        type="date"
                        className={`mt-1 w-full ${FIELD_CLASS}`}
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                      />
                    </div>
                    <div className="min-w-[190px]">
                      <div className={LABEL_TEXT}>
                        To
                      </div>
                      <input
                        type="date"
                        className={`mt-1 w-full ${FIELD_CLASS}`}
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                      />
                    </div>
                  </>
                )}

                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={resetFilterValues}
                >
                  Clear
                </button>
              </div>
              {filterBy === "appointment_type" && (
                <div className="text-xs text-slate-500">
                  {filterApptType !== "all"
                    ? appointmentTypeDescription(filterApptType)
                    : "Showing all appointment types."}
                </div>
              )}
              {!serviceLogLoading && !filteredServiceHistory.length && (
                <div className="text-sm text-slate-500">
                  No service history found.
                </div>
              )}
              {filteredServiceHistory.length > 0 && (
                <div className="text-xs text-slate-500">
                  Showing {filteredServiceHistory.length} of{" "}
                  {serviceHistory.length} results
                </div>
              )}
              {filteredServiceHistory.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="hidden md:grid md:grid-cols-8 gap-3 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Service</span>
                    <span>Appointment Type</span>
                    <span>Provider</span>
                    <span>Status</span>
                    <span>Price</span>
                    <span>Refunded</span>
                    <span>Net</span>
                    <span>Date</span>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {filteredServiceHistory.map((entry) => {
                      const rawType = entry?.client_type ?? entry?.appointment_type;
                      const normalizedType = rawType
                        ? normalizeAppointmentType(rawType)
                        : null;
                      const typeLabel = normalizedType
                        ? appointmentTypeLabel(normalizedType)
                        : "--";
                      const typeDescription = normalizedType
                        ? appointmentTypeDescription(normalizedType)
                        : "";
                      const refundedAmount = Number(entry.refunded_amount || 0);
                      const netAmount =
                        entry.net_price != null
                          ? Number(entry.net_price)
                          : Number(entry.price || 0) - refundedAmount;
                      const refundStatus = String(entry.refund_status || "none");
                      const showRefundChip =
                        refundStatus === "partial" || refundStatus === "full";
                      const refundLabel =
                        refundStatus === "full"
                          ? "Refunded"
                          : refundStatus === "partial"
                          ? "Partial refund"
                          : "";
                      return (
                        <div
                          key={entry.id}
                          className="px-4 py-3 text-sm text-slate-700 md:grid md:grid-cols-8 md:gap-3"
                        >
                          <div className="space-y-3 md:hidden">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Service
                                </div>
                                <div className="font-semibold text-slate-900 break-words">
                                  {entry.service || "--"}
                                </div>
                              </div>
                              {showRefundChip && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                  {refundLabel}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Appointment Type
                                </div>
                                <div className="break-words" title={typeDescription || undefined}>
                                  {typeLabel}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Provider
                                </div>
                                <div className="break-words">{entry.provider || "--"}</div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Status
                                </div>
                                <div>{formatServiceStatusLabel(entry.status)}</div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Date
                                </div>
                                <div>{formatShortDate(entry.date)}</div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Price
                                </div>
                                <div>
                                  {Number.isFinite(Number(entry.price))
                                    ? `$${Number(entry.price).toFixed(2)}`
                                    : "--"}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Refunded
                                </div>
                                <div>
                                  {Number.isFinite(refundedAmount)
                                    ? `$${refundedAmount.toFixed(2)}`
                                    : "--"}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Net
                                </div>
                                <div>
                                  {Number.isFinite(netAmount)
                                    ? `$${netAmount.toFixed(2)}`
                                    : "--"}
                                </div>
                              </div>
                            </div>
                          </div>
                          <span className="hidden font-semibold text-slate-900 md:flex md:items-center md:gap-2">
                            {entry.service || "--"}
                            {showRefundChip && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                {refundLabel}
                              </span>
                            )}
                          </span>
                          <span className="hidden md:block" title={typeDescription || undefined}>
                            {typeLabel}
                          </span>
                          <span className="hidden md:block">{entry.provider || "--"}</span>
                          <span className="hidden md:block">{formatServiceStatusLabel(entry.status)}</span>
                          <span className="hidden md:block">
                            {Number.isFinite(Number(entry.price))
                              ? `$${Number(entry.price).toFixed(2)}`
                              : "--"}
                          </span>
                          <span className="hidden md:block">
                            {Number.isFinite(refundedAmount)
                              ? `$${refundedAmount.toFixed(2)}`
                              : "--"}
                          </span>
                          <span className="hidden md:block">
                            {Number.isFinite(netAmount)
                              ? `$${netAmount.toFixed(2)}`
                              : "--"}
                          </span>
                          <span className="hidden md:block">{formatShortDate(entry.date)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === TAB_TICKETS && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px]">
                  <div className={LABEL_TEXT}>From</div>
                  <input
                    type="date"
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    value={ticketStartDate}
                    onChange={(event) => setTicketStartDate(event.target.value)}
                  />
                </div>
                <div className="min-w-[180px]">
                  <div className={LABEL_TEXT}>To</div>
                  <input
                    type="date"
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    value={ticketEndDate}
                    onChange={(event) => setTicketEndDate(event.target.value)}
                  />
                </div>
                <div className="min-w-[220px] flex-1">
                  <div className={LABEL_TEXT}>Search</div>
                  <input
                    type="text"
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                    value={ticketSearch}
                    onChange={(event) => setTicketSearch(event.target.value)}
                    placeholder="Ticket # or Type"
                  />
                </div>
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  onClick={() => {
                    loadClientTickets();
                    loadClientReviews();
                  }}
                  disabled={ticketRowsLoading}
                >
                  {ticketRowsLoading ? "Loading..." : "Search"}
                </button>
              </div>

              {ticketRowsError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {ticketRowsError}
                </div>
              )}
              {reviewRowsError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {reviewRowsError}
                </div>
              )}

              {ticketRowsLoading && (
                <div className="text-sm text-slate-500">Loading tickets...</div>
              )}
              {!ticketRowsLoading && reviewRowsLoading && (
                <div className="text-sm text-slate-500">Loading reviews...</div>
              )}

              {!ticketRowsLoading && filteredTicketRows.length === 0 && (
                <div className="text-sm text-slate-500">No tickets found.</div>
              )}

              {filteredTicketRows.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="hidden w-full table-fixed text-sm md:table">
                    <colgroup>
                      <col style={{ width: "13%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "11%" }} />
                    </colgroup>
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Ticket #</th>
                        <th className="px-4 py-2 text-left">Reason</th>
                        <th className="px-4 py-2 text-right">Amount</th>
                        <th className="px-4 py-2 text-left">Payment Method</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-left">Feedback</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredTicketRows.map((row) => {
                        const review = reviewByTicketId[row.ticket_id];
                        const isExpanded = Boolean(reviewExpanded?.[row.ticket_id]);
                        const hasReview = Boolean(review);
                        const needsFollowUp =
                          review?.overall_rating != null &&
                          Number(review.overall_rating) <= 3;
                        return (
                          <Fragment key={row.ticket_id}>
                            <tr
                              className="cursor-pointer hover:bg-slate-50"
                              onClick={() => handleViewTicket(row.ticket_id)}
                            >
                              <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                                {formatShortDate(row.date)}
                              </td>
                              <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                                {row.type_label || row.type}
                              </td>
                              <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                                <button
                                  type="button"
                                  className="text-sky-700 underline"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleViewTicket(row.ticket_id);
                                  }}
                                >
                                  {row.ticket_number || row.ticket_id}
                                </button>
                              </td>
                              <td className="px-4 py-2 text-slate-700 truncate">
                                {row.reason || "--"}
                              </td>
                              <td className="px-4 py-2 text-right text-slate-700 whitespace-nowrap">
                                {formatSignedMoney(row.amount)}
                              </td>
                              <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                                {row.payment_method || "--"}
                              </td>
                              <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                                {row.status_label || row.status}
                              </td>
                              <td className="px-4 py-2 text-slate-700">
                                {hasReview ? (
                                  <div className="space-y-1">
                                    {renderStars(review.overall_rating)}
                                    <div className="flex flex-wrap items-center gap-2">
                                      {needsFollowUp && (
                                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                                          Needs follow-up
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          toggleReviewExpanded(row.ticket_id);
                                        }}
                                        className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                                      >
                                        {isExpanded ? "Hide" : "Details"}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">--</span>
                                )}
                              </td>
                            </tr>
                            {hasReview && isExpanded && (
                              <tr className="bg-slate-50">
                                <td colSpan={8} className="px-4 py-4">
                                  <div className="space-y-3 text-sm text-slate-700">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                      <div className="flex flex-wrap items-center gap-3">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Technician
                                        </span>
                                        <span className="font-semibold text-slate-900">
                                          {review.technician_name || "Unknown"}
                                        </span>
                                        {needsFollowUp && (
                                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                                            Needs follow-up
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => openReviewDetail(review)}
                                        className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                                      >
                                        Full review
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Overall
                                        </div>
                                        {renderStars(review.overall_rating)}
                                      </div>
                                      <div>
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Salon
                                        </div>
                                        {renderStars(review.salon_rating)}
                                      </div>
                                      <div>
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Reception
                                        </div>
                                        {renderStars(review.reception_rating)}
                                      </div>
                                      <div>
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Service
                                        </div>
                                        {renderStars(review.service_rating)}
                                      </div>
                                      <div>
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Staff
                                        </div>
                                        {renderStars(review.staff_rating)}
                                      </div>
                                      {review.online_booking_rating != null && (
                                        <div>
                                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Online booking
                                          </div>
                                          {renderStars(review.online_booking_rating)}
                                        </div>
                                      )}
                                    </div>
                                    {Array.isArray(review.technician_ratings) &&
                                      review.technician_ratings.length > 0 && (
                                        <div className="space-y-2">
                                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Technician ratings
                                          </div>
                                          <div className="space-y-2">
                                            {review.technician_ratings.map((rating) => (
                                              <div
                                                key={rating.technician_user_id}
                                                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                                              >
                                                <div className="font-semibold text-slate-800">
                                                  {rating.technician_name ||
                                                    `Technician #${rating.technician_user_id}`}
                                                </div>
                                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                  <div>
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                      Service
                                                    </div>
                                                    {renderStars(rating.service_rating)}
                                                  </div>
                                                  <div>
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                      Staff
                                                    </div>
                                                    {renderStars(rating.staff_rating)}
                                                  </div>
                                                </div>
                                                {rating.comment && (
                                                  <div className="mt-2 text-xs text-slate-600 whitespace-pre-line">
                                                    {rating.comment}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    {(review.comment || review.comment_snippet) && (
                                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 whitespace-pre-line">
                                        {review.comment || review.comment_snippet}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="divide-y divide-slate-200 md:hidden">
                    {filteredTicketRows.map((row) => {
                      const review = reviewByTicketId[row.ticket_id];
                      const isExpanded = Boolean(reviewExpanded?.[row.ticket_id]);
                      const hasReview = Boolean(review);
                      const needsFollowUp =
                        review?.overall_rating != null &&
                        Number(review.overall_rating) <= 3;
                      return (
                        <div key={row.ticket_id} className="bg-white">
                          <button
                            type="button"
                            className="w-full space-y-3 px-4 py-3 text-left"
                            onClick={() => handleViewTicket(row.ticket_id)}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Ticket #
                                </div>
                                <div className="font-semibold text-sky-700 break-words">
                                  {row.ticket_number || row.ticket_id}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Date
                                </div>
                                <div className="text-sm text-slate-700">
                                  {formatShortDate(row.date)}
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm text-slate-700">
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Type
                                </div>
                                <div className="break-words">{row.type_label || row.type}</div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Status
                                </div>
                                <div className="break-words">{row.status_label || row.status}</div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Amount
                                </div>
                                <div>{formatSignedMoney(row.amount)}</div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Payment
                                </div>
                                <div className="break-words">{row.payment_method || "--"}</div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Reason
                                </div>
                                <div className="break-words">{row.reason || "--"}</div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Feedback
                                </div>
                                {hasReview ? (
                                  <div className="space-y-1">
                                    {renderStars(review.overall_rating)}
                                    <div className="flex flex-wrap items-center gap-2">
                                      {needsFollowUp && (
                                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                                          Needs follow-up
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          toggleReviewExpanded(row.ticket_id);
                                        }}
                                        className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                                      >
                                        {isExpanded ? "Hide" : "Details"}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">--</span>
                                )}
                              </div>
                            </div>
                          </button>
                          {hasReview && isExpanded && (
                            <div className="border-t border-slate-200 bg-slate-50 px-4 py-4">
                              <div className="space-y-3 text-sm text-slate-700">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="flex flex-wrap items-center gap-3">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Technician
                                    </span>
                                    <span className="font-semibold text-slate-900">
                                      {review.technician_name || "Unknown"}
                                    </span>
                                    {needsFollowUp && (
                                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                                        Needs follow-up
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => openReviewDetail(review)}
                                    className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                                  >
                                    Full review
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Overall
                                    </div>
                                    {renderStars(review.overall_rating)}
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Salon
                                    </div>
                                    {renderStars(review.salon_rating)}
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Reception
                                    </div>
                                    {renderStars(review.reception_rating)}
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Service
                                    </div>
                                    {renderStars(review.service_rating)}
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                      Staff
                                    </div>
                                    {renderStars(review.staff_rating)}
                                  </div>
                                  {review.online_booking_rating != null && (
                                    <div>
                                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Online booking
                                      </div>
                                      {renderStars(review.online_booking_rating)}
                                    </div>
                                  )}
                                </div>
                                {Array.isArray(review.technician_ratings) &&
                                  review.technician_ratings.length > 0 && (
                                    <div className="space-y-2">
                                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Technician ratings
                                      </div>
                                      <div className="space-y-2">
                                        {review.technician_ratings.map((rating) => (
                                          <div
                                            key={rating.technician_user_id}
                                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                                          >
                                            <div className="font-semibold text-slate-800">
                                              {rating.technician_name ||
                                                `Technician #${rating.technician_user_id}`}
                                            </div>
                                            <div className="mt-2 grid grid-cols-1 gap-2">
                                              <div>
                                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Service
                                                </div>
                                                {renderStars(rating.service_rating)}
                                              </div>
                                              <div>
                                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                  Staff
                                                </div>
                                                {renderStars(rating.staff_rating)}
                                              </div>
                                            </div>
                                            {rating.comment && (
                                              <div className="mt-2 text-xs text-slate-600 whitespace-pre-line">
                                                {rating.comment}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                {(review.comment || review.comment_snippet) && (
                                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 whitespace-pre-line">
                                    {review.comment || review.comment_snippet}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === TAB_GIFT_CARDS && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Gift Card History
                </h3>
              </div>
              {clientGiftCardsLoading && (
                <div className="text-sm text-slate-500">Loading gift cards...</div>
              )}
              {clientGiftCardsError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {clientGiftCardsError}
                </div>
              )}
              {!clientGiftCardsLoading && clientGiftCards.length === 0 && (
                <div className="text-sm text-slate-500">No gift cards found.</div>
              )}
              {clientGiftCards.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-8 gap-3 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Code</span>
                    <span>Owner</span>
                    <span>Purchaser</span>
                    <span>Purchased Date</span>
                    <span>Expired</span>
                    <span>Returned</span>
                    <span>Total Amount</span>
                    <span>Remaining</span>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {clientGiftCards.map((card) => (
                      <div
                        key={card.code}
                        className="grid grid-cols-8 gap-3 px-4 py-3 text-sm text-slate-700"
                      >
                        <button
                          type="button"
                          onClick={() => setGiftCardModalCode(card.code)}
                          className="text-sky-700 underline text-left"
                        >
                          {card.code}
                        </button>
                        <span>{card.owner || ""}</span>
                        <span>{card.purchaser || ""}</span>
                        <span>{formatShortDate(card.purchased_date)}</span>
                        <span>{card.expired || "No"}</span>
                        <span>{card.returned || "No"}</span>
                        <span>{formatMoney(card.total_amount)}</span>
                        <span>{formatMoney(card.remaining)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === TAB_REWARDS && (
            <div className="space-y-4">
              <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Rewards & Referrals
                  </h3>
                  <button
                    type="button"
                    onClick={loadRewards}
                    disabled={rewardsLoading}
                    className="text-xs text-slate-500 underline disabled:opacity-60"
                  >
                    {rewardsLoading ? "Loading..." : "Refresh"}
                  </button>
                </div>
                {rewardsError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {rewardsError}
                  </div>
                )}
                {!rewardsLoading && !rewardsError && rewardsData && (
                  <div className="space-y-3 text-sm text-slate-700">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        Points Balance:{" "}
                        <span className="font-semibold text-slate-900">
                          {rewardsPoints} pts
                        </span>{" "}
                        ({formatMoney(rewardsValue)})
                      </span>
                      <span className="text-xs text-slate-500">
                        Lifetime earned {rewardsData.lifetime_earned_points ?? 0} •
                        Redeemed {rewardsData.lifetime_redeemed_points ?? 0}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Referral Code
                      </span>
                      <InfoTooltip
                        text="Share this code with a friend. They enter it in their Client Profile → Rewards → Link Referral. After their first paid ticket is closed, you get the referral bonus points."
                        label="How to use this code"
                      />
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-semibold text-slate-900">
                        {rewardsData.referral_code || "--"}
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyReferralCode}
                        disabled={!rewardsData.referral_code}
                        className="text-xs text-sky-700 underline disabled:opacity-60"
                      >
                        Copy
                      </button>
                      {copyNotice && (
                        <span className="text-xs text-emerald-600">
                          {copyNotice}
                        </span>
                      )}
                    </div>
                    {rewardsData.referred_by_client_id ? (
                      <div className="text-xs text-slate-500">
                        Referred by Client #{rewardsData.referred_by_client_id}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500">
                        Not yet linked to a referrer.
                      </div>
                    )}
                    {canLinkReferral && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <span>Link Referral</span>
                          <InfoTooltip
                            text="Enter the code your friend gave you."
                            label="Link Referral"
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            value={referralInput}
                            onChange={(e) => {
                              setReferralInput(e.target.value);
                              setReferralLookup(null);
                              setReferralLookupError("");
                              setReferralBlockInfo(null);
                              setReferralNotice("");
                            }}
                            className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            placeholder="Enter referral code"
                          />
                          <button
                            type="button"
                            onClick={handleLookupReferral}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                          >
                            Validate
                          </button>
                          <button
                            type="button"
                            onClick={handleLinkReferral}
                            disabled={!referralLookup || referralLinking}
                            className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                          >
                            {referralLinking ? "Linking..." : "Link"}
                          </button>
                        </div>
                        {referralLookup && (
                          <div className="text-xs text-slate-600">
                            Referrer found:{" "}
                            <span className="font-semibold text-slate-900">
                              {referralLookup.referrer_name}
                            </span>
                            <span className="text-slate-400">
                              {" "}
                              (not linked yet)
                            </span>
                          </div>
                        )}
                        {referralLookupError && (
                          <div className="text-xs text-rose-600">
                            {referralLookupError}
                          </div>
                        )}
                        {referralLookupError && referralBlockedLabel && (
                          <div className="text-xs text-slate-500">
                            {referralBlockedLabel}
                          </div>
                        )}
                        {referralNotice && (
                          <div className="text-xs text-emerald-600">
                            {referralNotice}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Recent Transactions
                  </h3>
                  <span className="text-xs text-slate-500">Last 10</span>
                </div>
                {rewardsLoading && (
                  <div className="text-sm text-slate-500">
                    Loading transactions...
                  </div>
                )}
                {!rewardsLoading && rewardsTransactions.length === 0 && (
                  <div className="text-sm text-slate-500">
                    No reward transactions found.
                  </div>
                )}
                {rewardsTransactions.length > 0 && (
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <div className="grid grid-cols-4 gap-3 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span>Date</span>
                      <span>Type</span>
                      <span className="text-right">Points</span>
                      <span>Note</span>
                    </div>
                    <div className="divide-y divide-slate-200">
                      {rewardsTransactions.map((row) => {
                        const pointsDelta = Number(row.points_delta || 0);
                        const pointClass =
                          pointsDelta >= 0
                            ? "text-emerald-600"
                            : "text-rose-600";
                        const dateLabel =
                          row.posted_local_date || row.posted_at;
                        return (
                          <div
                            key={row.id}
                            className="grid grid-cols-4 gap-3 px-4 py-3 text-sm text-slate-700"
                          >
                            <span>{formatDateShort(dateLabel)}</span>
                            <span className="capitalize">
                              {formatRewardTxLabel(row.tx_type)}
                            </span>
                            <span className={`text-right font-semibold ${pointClass}`}>
                              {pointsDelta >= 0 ? "+" : ""}
                              {pointsDelta}
                            </span>
                            <span className="truncate">{row.note || "--"}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}

          {activeTab === TAB_EMAILS && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      Email Preferences
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Choose which optional emails this client should receive.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopyEmailPreferenceLink}
                      disabled={
                        emailPreferenceLinkLoading ||
                        !initialClient?.id ||
                        !String(email || hydratedClient?.email || "").trim()
                      }
                      className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {emailPreferenceLinkLoading
                        ? "Copying..."
                        : "Copy unsubscribe link"}
                    </button>
                    {emailPreferencesLoading && (
                      <span className="text-xs font-semibold text-slate-500">
                        Loading...
                      </span>
                    )}
                  </div>
                </div>
                {!String(email || hydratedClient?.email || "").trim() && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    Add an email address before sending client emails.
                  </div>
                )}
                {emailPreferencesError && (
                  <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {emailPreferencesError}
                  </div>
                )}
                {copyNotice && (
                  <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {copyNotice}
                  </div>
                )}
                <div className="mt-4 space-y-3">
                  {emailPreferenceRows.map((pref) => {
                    const isBlockedByAll =
                      pref.category !== "all_optional" && !allOptionalSubscribed;
                    const checked = isBlockedByAll ? false : pref.is_subscribed;
                    const disabled =
                      !canEditClient ||
                      Boolean(emailPreferencesSaving) ||
                      emailPreferencesLoading ||
                      isBlockedByAll;
                    return (
                      <div
                        key={pref.category}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900">
                            {pref.label}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {pref.description}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-400">
                            {isBlockedByAll
                              ? "Stopped because all optional emails are off."
                              : `${preferenceSourceLabel(pref.source)}${
                                  pref.updated_at_utc
                                    ? ` on ${formatDateShort(pref.updated_at_utc)}`
                                    : ""
                                }`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleEmailPreferenceToggle(pref)}
                          disabled={disabled}
                          className={`relative h-8 w-16 rounded-full border transition disabled:cursor-not-allowed disabled:opacity-60 ${
                            checked
                              ? "border-emerald-300 bg-emerald-100"
                              : "border-slate-300 bg-white"
                          }`}
                          aria-pressed={checked}
                          aria-label={`${checked ? "Turn off" : "Turn on"} ${pref.label}`}
                        >
                          <span
                            className={`absolute top-1 h-6 w-6 rounded-full shadow transition ${
                              checked
                                ? "left-9 bg-emerald-500"
                                : "left-1 bg-slate-300"
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Receipts, password resets, and critical account emails may still be sent.
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  Email Messages
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    loadEmailPreferences();
                    loadEmailMessages();
                  }}
                  disabled={emailMessagesLoading || emailPreferencesLoading}
                  className="text-xs text-slate-500 underline disabled:opacity-60"
                >
                  {emailMessagesLoading || emailPreferencesLoading ? "Loading..." : "Refresh"}
                </button>
              </div>
              {emailMessagesError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {emailMessagesError}
                </div>
              )}
              {!emailMessagesLoading && !emailMessagesError && emailMessages.length === 0 && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No email messages found for this client yet.
                </div>
              )}
              {emailMessages.length > 0 && (
                <div className="space-y-3">
                  {emailMessages.map((message) => {
                    const isExpanded = Boolean(expandedEmailMessages[message.id]);
                    const timestamp =
                      message.direction === "inbound"
                        ? message.received_at_utc || message.created_at_utc
                        : message.sent_at_utc || message.created_at_utc;
                    const addressLine =
                      message.direction === "inbound"
                        ? `From ${message.from_email || "client"}`
                        : `To ${message.to_email || "client"}`;
                    return (
                      <div
                        key={message.id}
                        className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-slate-900">
                                {emailMessageTypeLabel(message.message_type)}
                              </span>
                              <span
                                className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${emailStatusClass(
                                  message.status
                                )}`}
                              >
                                {message.status || "sent"}
                              </span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-slate-500">
                                {message.direction === "inbound" ? "Incoming" : "Outgoing"}
                              </span>
                            </div>
                            <div className="mt-1 truncate font-medium text-slate-800">
                              {message.subject || "(No subject)"}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {formatDateTime(timestamp)} · {addressLine}
                              {message.created_by_name ? ` · By ${message.created_by_name}` : ""}
                            </div>
                          </div>
                          {message.body_text && (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedEmailMessages((prev) => ({
                                  ...prev,
                                  [message.id]: !prev[message.id],
                                }))
                              }
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              {isExpanded ? "Hide" : "View"}
                            </button>
                          )}
                        </div>
                        {(message.body_preview || message.error) && (
                          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                            {message.error ? `Error: ${message.error}` : message.body_preview}
                          </div>
                        )}
                        {isExpanded && message.body_text && (
                          <div className="mt-3 whitespace-pre-wrap rounded-xl border border-slate-200 bg-white px-3 py-3 text-xs leading-relaxed text-slate-700">
                            {message.body_text}
                          </div>
                        )}
                        {message.reply_to_email && (
                          <div className="mt-2 text-xs text-slate-500">
                            Reply tracking: {message.reply_to_email}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === TAB_NOTES && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800">
                  Client Notes
                </h3>
                {canEditClient && (
                  <button
                    type="button"
                    onClick={handleOpenNoteEditor}
                    className="h-8 w-8 rounded-full bg-emerald-500 text-white text-lg font-semibold flex items-center justify-center hover:bg-emerald-600"
                    aria-label="Add note"
                  >
                    +
                  </button>
                )}
              </div>
              {notesError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {notesError}
                </div>
              )}
              {noteEditorOpen && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    rows={3}
                    className={`w-full ${FIELD_CLASS}`}
                    placeholder="Write a note..."
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleCloseNoteEditor}
                      className={BTN_OUTLINE}
                      disabled={noteSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveNote}
                      className={BTN_PRIMARY}
                      disabled={noteSaving || !noteDraft.trim()}
                    >
                      {noteSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
              <ClientNotesPanel
                ref={notesPanelRef}
                clientId={initialClient?.id}
                mode="embedded"
                readOnly={!canEditClient}
              />
            </div>
          )}
        </div>

        <GiftCardDetailsModal
          isOpen={Boolean(giftCardModalCode)}
          code={giftCardModalCode}
          onClose={() => setGiftCardModalCode(null)}
        />

        <SBINModal
          isOpen={emailAppointmentOpen}
          onClose={closeEmailAppointmentModal}
          title="Email Schedule to Client"
          size="sm"
          fitContent={true}
          closeOnBackdrop={true}
          closeOnEscape={true}
          bodyClassName="space-y-4"
          footer={
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                className={BTN_OUTLINE}
                onClick={closeEmailAppointmentModal}
                disabled={emailAppointmentSending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={BTN_PRIMARY}
                onClick={handleSendAppointmentEmail}
                disabled={emailAppointmentSending}
              >
                {emailAppointmentSending ? "Sending..." : "Send Email"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Confirm or enter the email address for the client.
            </p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">
                {futureAppointments.length} future appointment
                {futureAppointments.length === 1 ? "" : "s"}
              </div>
              <div>
                This email will include the full list of future appointments shown
                on this tab.
              </div>
            </div>
            <div className="space-y-2">
              <label className={LABEL_TEXT}>Email</label>
              <input
                type="email"
                className={`w-full ${FIELD_CLASS}`}
                value={emailAppointmentValue}
                onChange={(event) => {
                  setEmailAppointmentValue(event.target.value);
                  if (emailAppointmentError) {
                    setEmailAppointmentError("");
                  }
                }}
                placeholder="client@example.com"
                autoFocus
                disabled={emailAppointmentSending}
              />
            </div>
            {emailAppointmentError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {emailAppointmentError}
              </div>
            ) : null}
            {!currentClientEmail ? (
              <p className="text-sm text-slate-600">
                NOTE: The email address entered here will be saved to the client&apos;s
                profile after the schedule email is sent.
              </p>
            ) : null}
          </div>
        </SBINModal>

        <SBINModal
          isOpen={reviewDetailOpen}
          onClose={closeReviewDetail}
          title="Review Details"
          size="md"
          fitContent={true}
          closeOnBackdrop={true}
          closeOnEscape={true}
        >
          {!reviewDetail ? (
            <div className="text-sm text-slate-500">No review selected.</div>
          ) : (
            <div className="space-y-4 text-sm text-slate-700">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Visit
                </div>
                <div className="mt-1 text-base font-semibold text-slate-900">
                  Ticket #{reviewDetail.ticket_id}
                </div>
                <div className="text-xs text-slate-500">
                  {reviewDetail.visit_date || "Date unavailable"}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Overall
                  </div>
                  {renderStars(reviewDetail.overall_rating)}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Salon
                  </div>
                  {renderStars(reviewDetail.salon_rating)}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Reception
                  </div>
                  {renderStars(reviewDetail.reception_rating)}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Service
                  </div>
                  {renderStars(reviewDetail.service_rating)}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff
                  </div>
                  {renderStars(reviewDetail.staff_rating)}
                </div>
                {reviewDetail.online_booking_rating != null && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Online booking
                    </div>
                    {renderStars(reviewDetail.online_booking_rating)}
                  </div>
                )}
              </div>
              {Array.isArray(reviewDetail.technician_ratings) &&
                reviewDetail.technician_ratings.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Technician ratings
                    </div>
                    <div className="space-y-2">
                      {reviewDetail.technician_ratings.map((rating) => (
                        <div
                          key={rating.technician_user_id}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                        >
                          <div className="font-semibold text-slate-800">
                            {rating.technician_name ||
                              `Technician #${rating.technician_user_id}`}
                          </div>
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Service
                              </div>
                              {renderStars(rating.service_rating)}
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Staff
                              </div>
                              {renderStars(rating.staff_rating)}
                            </div>
                          </div>
                          {rating.comment && (
                            <div className="mt-2 text-xs text-slate-600 whitespace-pre-line">
                              {rating.comment}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              {(reviewDetail.comment || reviewDetail.comment_snippet) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 whitespace-pre-line">
                  {reviewDetail.comment || reviewDetail.comment_snippet}
                </div>
              )}
            </div>
          )}
        </SBINModal>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex w-full items-center gap-3 sm:w-auto">
            {canOpenNewAppointment && (
              <button
                type="button"
                onClick={() => onOpenNewAppointment(appointmentClient)}
                className={`${BTN_OUTLINE} w-full sm:w-auto`}
              >
                New Appointment
              </button>
            )}
            {activeTab === TAB_FUTURE_APPTS && futureAppointments.length > 0 && (
              <button
                type="button"
                onClick={openEmailAppointmentModal}
                className={`${BTN_OUTLINE} w-full sm:w-auto`}
                disabled={emailAppointmentSending}
              >
                Email
              </button>
            )}
          </div>
          <div className="flex w-full flex-wrap items-center justify-end gap-3 sm:w-auto sm:flex-nowrap">
            {isReadOnlyTab ? (
              <button
                type="button"
                onClick={onClose}
                className={`${BTN_OUTLINE} w-full sm:w-auto`}
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className={`${BTN_OUTLINE} flex-1 sm:flex-none`}
                >
                  {canEditClient ? "Cancel" : "Close"}
                </button>
                {canEditClient && (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || (!isEdit && !createIsValid)}
                    className={`${BTN_PRIMARY} flex-1 sm:flex-none`}
                  >
                    {saving
                      ? "Saving..."
                      : isEdit
                      ? "Save Changes"
                      : "Create Client"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}







